import JsPDF from "jspdf";
import "jspdf-autotable";
import writtenNumber from "written-number";
import apiClient from "@/services/axios";
import { formatFeeName } from "@/utilities/settings";
import { pdfHeader } from "@/utilities/pdfUtils";
import { getAllLevels } from "@/config/LevelConstants";

/** Bottom margin (mm) reserved on each page for legend + page number. Tables will break before this area. */
const LEGEND_FOOTER_HEIGHT_MM = 28;

export class PdfGenerator {
  constructor(context) {
    this.context = context;
  }

  formatMontant(montant) {
    if (!Number(montant)) return false;
    return parseFloat(montant).toFixed(3);
  }

  /**
   * Registration column fill color: green (paid), red (unpaid), yellow (partial), blue (paid with discount only).
   * Blue is shown only for paid amounts with a reduction; unpaid (even with reduction) stays red.
   * @param {Object} student - student row with inscription, inscriptionPaid?, inscriptionDiscounted?
   * @param {string} paidColor - green
   * @param {string} color - red
   * @param {string} discountColor - blue (only for paid + discount)
   * @returns {string} fillColor hex
   */
  getInscriptionFillColor(student, paidColor, color, discountColor) {
    const partialColor = "#fff9c4";
    const whiteColor = "#ffffff";
    const hasUnpaid = Number(student.inscription) > 0;

    if (!hasUnpaid) {
      // inscription is 0: either fully paid OR no registration fee at all
      // Check if inscriptionPaid > 0 to distinguish
      if (Number(student.inscriptionPaid) > 0) {
        // Fully paid: green, or blue if paid with discount
        return student.inscriptionDiscounted ? discountColor : paidColor;
      }
      // No registration fee at all (classroom.inscription is false/null) -> white
      return whiteColor;
    }
    if (Number(student.inscriptionPaid) > 0) return partialColor; // partial -> yellow
    // Unpaid (with or without reduction) -> red
    return color;
  }

  /**
   * Registration column content: show paid amount when fully paid, else unpaid amount.
   * @param {Object} student - student row with inscription, inscriptionPaid?, inscriptionDiscounted?
   * @returns {string} value to display in the cell
   */
  getInscriptionContent(student) {
    const hasUnpaid = Number(student.inscription) > 0;
    if (hasUnpaid) return this.formatMontant(student.inscription) || "0.000";
    // Fully paid: show paid amount (whether with or without discount)
    if (Number(student.inscriptionPaid) > 0)
      return this.formatMontant(student.inscriptionPaid) || "0.000";
    return "0";
  }

  /**
   * Draw the color legend at the bottom of the current page (above page number).
   * Green: Paid, Red: Unpaid, Yellow: Paid partially, Blue: Paid with discount.
   */
  drawLegendOnPage(doc, pageWidth, pageHeight, t) {
    const legendY = pageHeight - 20;
    const boxSize = 4;
    const boxHeight = 3;
    const itemGap = 12;
    const items = [
      { color: "#c8e6c9", key: "scolaritePdf.legendPaid" },
      { color: "#fcaa97", key: "scolaritePdf.legendUnpaid" },
      { color: "#fff9c4", key: "scolaritePdf.legendPaidPartially" },
      { color: "#b3d9ff", key: "scolaritePdf.legendPaidWithDiscount" },
    ];
    doc.setFontSize(9);
    doc.setTextColor(0, 0, 0);
    let x = 15;
    items.forEach((item) => {
      const hex = item.color;
      const r = parseInt(hex.slice(1, 3), 16);
      const g = parseInt(hex.slice(3, 5), 16);
      const b = parseInt(hex.slice(5, 7), 16);
      doc.setFillColor(r, g, b);
      doc.rect(x, legendY - boxHeight / 2, boxSize, boxHeight, "F");
      doc.text(t(item.key), x + boxSize + 2, legendY + 0.8);
      x += boxSize + 3 + doc.getTextWidth(t(item.key)) + itemGap;
    });
  }

  async generatePdf() {
    if (!(await this.context.packHasBtnAccess("pEtatImp"))) return;

    this.context.$gtag.event("Imp Etat des impayés", {
      event_category: "Impression PDF",
      event_label: "comptabilite:Etat des impayés",
      value: 1,
    });

    let selectMonth = document.getElementById("selectMonth").innerText;

    const title = this.context.$i18n.t("encaissement.stateOfUnpaid");
    const locale = this.context.$i18n.locale;
    const isArabic = locale === "ar-AR";

    const doc = pdfHeader(this.context.settings, {
      title: title,
      subTitle: selectMonth,
      locale: locale,
    });

    // Group by parent to merge tuition and activity fees
    const parentMap = new Map();

    for (let paymentRow of this.context.filtredTable) {
      const parentKey = `${paymentRow.parent.fatherName}_${paymentRow.parent.fatherPhone}`;

      if (!parentMap.has(parentKey)) {
        parentMap.set(parentKey, {
          fatherName: paymentRow.parent.fatherName,
          fatherPhone: paymentRow.parent.fatherPhone,
          children: new Map(), // Map of child -> fees
        });
      }

      const parentData = parentMap.get(parentKey);
      const childKey = paymentRow.student;

      if (!parentData.children.has(childKey)) {
        parentData.children.set(childKey, []);
      }

      // Collect unpaid fees for this child (exclude 0 amounts: inscription 0, full discount, etc.)
      if (paymentRow.fees) {
        for (let fee of paymentRow.fees) {
          if (fee.fullyPaid === false) {
            let feeName = formatFeeName(fee.feeName, paymentRow.daily);
            let feeAmount = fee.remaining
              ? fee.remaining
              : fee.feeAfterReduction;
            const amountNum = parseFloat(feeAmount);
            if (amountNum <= 0) continue; // do not display 0 or negative (e.g. full discount)
            parentData.children.get(childKey).push({
              feeName: feeName + " - " + paymentRow.name,
              amount: amountNum,
            });
          }
        }
      }
    }

    const t = this.context.$i18n.t.bind(this.context.$i18n);

    // Single source of truth: LTR column order = [ClientName, Phone, Child, Fees, Amount, Total]
    const headerColumnOrder = (arr) => (isArabic ? arr.slice().reverse() : arr);

    let somme = 0;
    const pageSize = doc.internal.pageSize;
    const pageHeightMm = pageSize.height;
    const pageMarginBottom = 28;
    const rowHeight = 7;
    const startY = 38;
    // We paginate manually to ensure no autoTable page-break happens inside a block that uses rowSpan.
    // Reserve space for the table head and a small safety buffer so body rows never overflow and trigger an internal page break.
    const estimatedHeadHeight = rowHeight;
    const safetyReserve = rowHeight * 2;
    const usableBodyHeight =
      pageHeightMm -
      pageMarginBottom -
      startY -
      estimatedHeadHeight -
      safetyReserve;
    const maxRowsPerPage = Math.max(
      1,
      Math.floor(usableBodyHeight / rowHeight)
    );

    // Build one body per page — rowspan never crosses because we draw one table per page.
    const pageChunks = [];
    let currentPageRows = [];
    let currentRowsOnPage = 0;

    const pushBlock = (
      parentData,
      parentTotalFormatted,
      rows,
      targetPageRows
    ) => {
      if (rows.length === 0) return;
      let i = 0;
      while (i < rows.length) {
        const childName = rows[i].childName;
        let j = i;
        while (j < rows.length && rows[j].childName === childName) j++;
        const childRowSpan = j - i;

        const childCell = {
          content: childName,
          rowSpan: childRowSpan,
          styles: { valign: "middle" },
        };

        // First row of this child group
        if (i === 0) {
          const firstRowLtr = [
            {
              content: parentData.fatherName,
              rowSpan: rows.length,
              styles: { valign: "middle" },
            },
            {
              content: parentData.fatherPhone,
              rowSpan: rows.length,
              styles: { valign: "middle" },
            },
            childCell,
            rows[i].feeName,
            rows[i].feeAmount,
            {
              content: parentTotalFormatted,
              rowSpan: rows.length,
              styles: { valign: "middle" },
            },
          ];
          targetPageRows.push(headerColumnOrder(firstRowLtr));
        } else {
          // New child group within same parent block/page
          targetPageRows.push(
            headerColumnOrder([childCell, rows[i].feeName, rows[i].feeAmount])
          );
        }

        // Remaining rows for same child: omit child cell so columns align under active rowspans
        for (let k = i + 1; k < j; k++) {
          targetPageRows.push(
            headerColumnOrder([rows[k].feeName, rows[k].feeAmount])
          );
        }

        i = j;
      }
    };

    for (let [parentKey, parentData] of parentMap) {
      const childrenEntries = Array.from(parentData.children.entries()).filter(
        ([_, fees]) => fees.length > 0
      );
      if (childrenEntries.length === 0) continue;

      let parentTotal = 0;
      childrenEntries.forEach(([_, fees]) => {
        fees.forEach((fee) => {
          parentTotal += fee.amount;
        });
      });
      somme += parentTotal;

      const parentTotalFormatted = parseFloat(parentTotal).toFixed(3);

      const allFeeRows = [];
      childrenEntries.forEach(([childName, fees]) => {
        fees.forEach((fee) => {
          const feeName = fee.feeName;
          const feeAmount = parseFloat(
            fee.amount || fee.feeAfterReduction || 0
          ).toFixed(3);
          allFeeRows.push({ childName, feeName, feeAmount });
        });
      });

      let offset = 0;
      while (offset < allFeeRows.length) {
        const rowsLeftOnPage = maxRowsPerPage - currentRowsOnPage;
        if (rowsLeftOnPage <= 0) {
          pageChunks.push(currentPageRows);
          currentPageRows = [];
          currentRowsOnPage = 0;
          continue;
        }

        const take = Math.min(rowsLeftOnPage, allFeeRows.length - offset);
        const chunk = allFeeRows.slice(offset, offset + take);
        pushBlock(parentData, parentTotalFormatted, chunk, currentPageRows);

        currentRowsOnPage += take;
        offset += take;
        if (offset < allFeeRows.length) {
          // Parent still has rows left: continue on the next page.
          pageChunks.push(currentPageRows);
          currentPageRows = [];
          currentRowsOnPage = 0;
        }
      }
    }
    if (currentPageRows.length > 0) pageChunks.push(currentPageRows);
    if (pageChunks.length === 0) pageChunks.push([]);

    const langMap = {
      "fr-FR": "fr",
      "en-US": "en",
      "ar-AR": "ar",
      "sw-KE": "sw",
    };
    const writtenNumberLang = langMap[locale] || "fr";
    const currencySuffix = t("encaissement.pdfCurrencySuffix");

    // Footer: LTR = [Total, writtenAmount colSpan, Total Amount]; RTL (Arabic) = reversed | 6 columns
    const footerRow = [
      t("encaissement.pdfTotal"),
      {
        colSpan: 4,
        content:
          writtenNumber(parseFloat(somme).toFixed(3), {
            lang: writtenNumberLang,
          }) +
          " " +
          currencySuffix,
        styles: { halign: "center" },
      },
      parseFloat(somme).toFixed(3),
    ];
    const orderedFooter = isArabic ? footerRow.slice().reverse() : footerRow;

    const tableHead = [
      headerColumnOrder([
        t("encaissement.pdfTableHeaders.clientName"),
        t("encaissement.pdfTableHeaders.telephone"),
        t("encaissement.pdfTableHeaders.child"),
        t("encaissement.pdfTableHeaders.fees"),
        t("encaissement.pdfTableHeaders.amount"),
        t("encaissement.pdfTableHeaders.total"),
      ]),
    ];
    const tableColumnStyles = isArabic
      ? {
          0: { halign: "center", valign: "middle" },
          1: { halign: "right" },
          2: { halign: "right" },
          3: { halign: "right" },
          4: { halign: "center", valign: "middle" },
          5: { halign: "center", valign: "middle" },
        }
      : {
          0: { halign: "center", valign: "middle" },
          1: { halign: "center", valign: "middle" },
          2: { halign: "left" },
          3: { halign: "left" },
          4: { halign: "left" },
          5: { halign: "center", valign: "middle" },
        };

    // One table per page — rowspan never crosses a page.
    for (let p = 0; p < pageChunks.length; p++) {
      if (p > 0) {
        doc.addPage();
        pdfHeader(this.context.settings, {
          title,
          subTitle: selectMonth,
          locale,
          existingDoc: doc,
        });
      }
      doc.autoTable({
        theme: "grid",
        // Prevent multi-line wrapping (variable row heights) which can force internal page breaks
        // and therefore break rowspan rendering. Ellipsize keeps row height predictable.
        styles: {
          font: "Amiri",
          halign: isArabic ? "right" : "left",
          overflow: "ellipsize",
        },
        headStyles: { halign: isArabic ? "right" : "left" },
        bodyStyles: { halign: isArabic ? "right" : "left" },
        startY,
        head: tableHead,
        body: pageChunks[p],
        foot: p === pageChunks.length - 1 ? [orderedFooter] : undefined,
        columnStyles: tableColumnStyles,
      });
    }
    //page numbering
    const pages = doc.internal.getNumberOfPages();
    const pageWidthMm = pageSize.width;
    doc.setFontSize(8); //Optional

    for (let j = 1; j < pages + 1; j++) {
      let horizontalPos = pageWidthMm / 2;
      let verticalPos = pageHeightMm - 10;
      doc.setPage(j);
      const pageText = this.context.$i18n.t("pdf.pageNumber", {
        current: j,
        total: pages,
      });
      doc.text(pageText, horizontalPos, verticalPos, {
        align: "center",
      });
    }
    //end page numbering

    this.context.pdfLoading = false;
    const fileName = this.context.$i18n.t("encaissement.pdfFileName") + ".pdf";
    doc.save(fileName);
  }

  async generatePdfScolarite() {
    if (!(await this.context.packHasBtnAccess("pEtatImpScolarite"))) return;

    this.context.pdfLoading = true;

    // Get locale and translation function
    const locale = this.context.$i18n.locale;
    const t = this.context.$i18n.t.bind(this.context.$i18n);
    const isArabic = locale === "ar-AR";
    // Original table order was for Arabic (RTL), so reverse for non-Arabic languages
    const reverseIfNotArabic = (arr) =>
      !isArabic ? arr.slice().reverse() : arr;
    // Reverse column styles to match reversed column order for non-Arabic languages
    const reverseColumnStyles = (styles, numColumns) => {
      if (isArabic) return styles;
      const reversed = {};
      for (let i = 0; i < numColumns; i++) {
        reversed[i] = styles[numColumns - 1 - i];
      }
      return reversed;
    };

    let imgData;
    let arrayOfImages = this.context.settings.image;
    for (let i = 0; i < arrayOfImages.length; i++) {
      if (this.context.settings.activeBuilding === arrayOfImages[i].db) {
        imgData = new Image();
        imgData.src = arrayOfImages[i].logo;
      }
    }

    let impayeData;

    await apiClient
      .get("/payment/etatImpaye/scolarite")
      .then((res) => (impayeData = res.data))
      .catch((e) => {
        console.error(e);
        this.context.$message.error(this.context.$i18n.t("error.error"));
      });

    // Get country code from context
    const activeBuilding = this.context.user.building.find(
      (el) => el.dbName == this.context.settings.activeBuilding
    );
    const countryCode = activeBuilding?.countryCode || "TN";

    // Get all levels using LevelConstants helper
    const levels = getAllLevels(t, countryCode);

    const doc = new JsPDF({
      orientation: "p",
      unit: "mm",
      format: "a3",
    });

    const pdf_width = doc.internal.pageSize.getWidth();
    const tableMarginHorizontal = 10;
    const fullTableWidth = pdf_width - tableMarginHorizontal * 2;

    const cellToDelete = {};

    for (const level of impayeData.levels) {
      if (level.classrooms.length === 0) continue;

      try {
        const logoX = isArabic ? 10 : pdf_width - 30; // Left for Arabic, right for others
        doc.addImage(imgData, "JPEG", logoX, 8, 20, 20);
      } catch (error) {
        console.log(error);
      }

      doc.setFont("Amiri", "normal");
      doc.setFontSize(10);

      const options = {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
      };

      doc.text(
        new Date().toLocaleDateString(locale, options),
        isArabic ? pdf_width - 17 : 17,
        8,
        {
          align: isArabic ? "right" : "left",
        }
      );

      doc.text(this.context.schoolarYearName, pdf_width / 2, 8, {
        align: "center",
      });

      doc.setFontSize(15);
      doc.setFont("Amiri", "Bold");
      doc.setTextColor("#505050");
      let text =
        t("encaissement.schoolingStateOfUnpaid") +
        " - " +
        (levels[String(level.level)] || level.level);
      doc.text(text, pdf_width / 2, 22, {
        align: "center",
      });

      for (const classroom of level.classrooms) {
        const monthlyStudents = (classroom.students || []).filter(
          (s) => s.mode === "M"
        );
        const trancheStudents = (classroom.students || []).filter(
          (s) => s.mode === "T"
        );

        const color = "#fcaa97"; // red for unpaid
        const paidColor = "#c8e6c9"; // green for paid
        const discountColor = "#b3d9ff"; // blue only for paid with discount
        // Blue = paid + discount. Unpaid/partial with discount stay red/yellow.
        // Global (full-year): only the monthly fee column can be blue.
        // Per-month/tranche: cell is blue only when paid AND discounted.

        // Month names translation
        const monthNames = [
          t("months.june"),
          t("months.may"),
          t("months.april"),
          t("months.march"),
          t("months.february"),
          t("months.january"),
          t("months.december"),
          t("months.november"),
          t("months.october"),
          t("months.september"),
        ];

        // ==============================================
        // TABLE 1: MONTHLY STUDENTS
        // ==============================================
        if (monthlyStudents.length > 0) {
          let array = [
            // Title row with class name
            reverseIfNotArabic([
              {
                content:
                  t("encaissement.monthlyPayment") + " - " + classroom.name,
                colSpan: 16,
                styles: {
                  fontStyle: "Bold",
                  fontSize: 10,
                  fillColor: "#e8f4f8",
                },
              },
            ]),
            reverseIfNotArabic([
              {
                content: t("paymentStatus.totalPaye"),
                styles: { fontStyle: "Bold", fontSize: 8 },
              },
              {
                content: t("paymentStatus.totalNonPaye"),
                styles: { fontStyle: "Bold", fontSize: 8 },
              },
              {
                content: monthNames[0],
                styles: { fontStyle: "Bold", fontSize: 9 },
              },
              {
                content: monthNames[1],
                styles: { fontStyle: "Bold", fontSize: 9 },
              },
              {
                content: monthNames[2],
                styles: { fontStyle: "Bold", fontSize: 9 },
              },
              {
                content: monthNames[3],
                styles: { fontStyle: "Bold", fontSize: 9 },
              },
              {
                content: monthNames[4],
                styles: { fontStyle: "Bold", fontSize: 9 },
              },
              {
                content: monthNames[5],
                styles: { fontStyle: "Bold", fontSize: 9 },
              },
              {
                content: monthNames[6],
                styles: { fontStyle: "Bold", fontSize: 9 },
              },
              {
                content: monthNames[7],
                styles: { fontStyle: "Bold", fontSize: 9 },
              },
              {
                content: monthNames[8],
                styles: { fontStyle: "Bold", fontSize: 9 },
              },
              {
                content: monthNames[9],
                styles: { fontStyle: "Bold", fontSize: 9 },
              },
              {
                content: t("scolaritePdf.registration"),
                styles: { fontStyle: "Bold", fontSize: 9 },
              },
              {
                content: levels[String(level.level)] || level.level,
                styles: { fontStyle: "Bold", fontSize: 9 },
              },
              {
                content: this.context.schoolarYearName,
                colSpan: 2,
                styles: { fontStyle: "Bold", fontSize: 9 },
              },
            ]),
          ];

          let arr = [];
          let studentId = 1;

          // Month columns 1..10 = months 6,5,4,3,2,1,12,11,10,9 (array order for RTL; LTR reverses so Registration appears before September)
          const monthKeysByCol = [6, 5, 4, 3, 2, 1, 12, 11, 10, 9];

          for (const student of monthlyStudents) {
            // Calculate inscription paid for this student
            const studentInscriptionPaid =
              Number(student.inscription) > 0
                ? Number(student.inscriptionPaid) || 0
                : Number(student.inscriptionPaid) || 0;

            // Total paid for the year (months paid + partial paid + inscription paid)
            const studentTotalPaidYear =
              monthKeysByCol.reduce(
                (acc, m) =>
                  acc +
                  (student.monthsPaid?.[m] || 0) +
                  (student.monthsPartialPaid?.[m] || 0),
                0
              ) + studentInscriptionPaid;

            const getMonthContent = (monthKey) => {
              const unpaid = this.formatMontant(
                student.monthsUnpaid?.[monthKey]
              );
              const paid = this.formatMontant(student.monthsPaid?.[monthKey]);
              // When partial we use two rows (remaining + paid), so no combined text here
              return unpaid || paid || "0.000";
            };

            // Full-year discount = only monthly fee column blue, not each month cell.
            const isMonthDiscounted = (monthKey) =>
              !!student.monthsDiscounted?.[monthKey];
            const allMonthsHaveDiscount = monthKeysByCol.every(
              (m) => student.monthsDiscounted?.[m]
            );
            const isFullYearDiscount =
              student.reductionGlobal || allMonthsHaveDiscount;
            const getMonthFillColor = (monthKey) => {
              if (this.formatMontant(student.monthsUnpaid?.[monthKey]))
                return color;
              if (student.monthsPartialPaid?.[monthKey]) return "#fff9c4";
              if (this.formatMontant(student.monthsPaid?.[monthKey]))
                return !isFullYearDiscount && isMonthDiscounted(monthKey)
                  ? discountColor
                  : paidColor;
              return "#ffffff";
            };

            const hasPartialInMonth = (monthKey) =>
              !!this.formatMontant(student.monthsPartialPaid?.[monthKey]);
            const hasPartialInscription =
              Number(student.inscription) > 0 &&
              Number(student.inscriptionPaid) > 0;
            const hasAnyPartial =
              monthKeysByCol.some(hasPartialInMonth) || hasPartialInscription;

            // Total unpaid for the year (months unpaid + inscription unpaid)
            const studentTotalUnpaidYear =
              (student.montantTotalMonths || 0) + (Number(student.inscription) || 0);

            if (!hasAnyPartial) {
              // Single row: total paid (green), total unpaid (red), months, Registration before September, then rest
              arr = reverseIfNotArabic([
                {
                  content: this.formatMontant(studentTotalPaidYear) || "0.000",
                  styles: { fontSize: 8, fillColor: paidColor },
                },
                {
                  content:
                    this.formatMontant(studentTotalUnpaidYear) || "0.000",
                  styles: { fontSize: 8, fillColor: color },
                },
                {
                  content: getMonthContent(6),
                  styles: {
                    fontSize: 8,
                    fillColor: getMonthFillColor(6),
                  },
                },
                {
                  content: getMonthContent(5),
                  styles: { fontSize: 8, fillColor: getMonthFillColor(5) },
                },
                {
                  content: getMonthContent(4),
                  styles: { fontSize: 8, fillColor: getMonthFillColor(4) },
                },
                {
                  content: getMonthContent(3),
                  styles: { fontSize: 8, fillColor: getMonthFillColor(3) },
                },
                {
                  content: getMonthContent(2),
                  styles: { fontSize: 8, fillColor: getMonthFillColor(2) },
                },
                {
                  content: getMonthContent(1),
                  styles: { fontSize: 8, fillColor: getMonthFillColor(1) },
                },
                {
                  content: getMonthContent(12),
                  styles: { fontSize: 8, fillColor: getMonthFillColor(12) },
                },
                {
                  content: getMonthContent(11),
                  styles: { fontSize: 8, fillColor: getMonthFillColor(11) },
                },
                {
                  content: getMonthContent(10),
                  styles: { fontSize: 8, fillColor: getMonthFillColor(10) },
                },
                {
                  content: getMonthContent(9),
                  styles: { fontSize: 8, fillColor: getMonthFillColor(9) },
                },
                {
                  content: this.getInscriptionContent(student),
                  styles: {
                    fontSize: 8,
                    fillColor: this.getInscriptionFillColor(
                      student,
                      paidColor,
                      color,
                      discountColor
                    ),
                  },
                },
                {
                  content: student.monthly,
                  styles: {
                    fillColor: isFullYearDiscount ? discountColor : "#ffffff",
                  },
                },
                { content: student.name },
                { content: studentId++ },
              ]);
              array.push(arr);
              continue;
            }

            // Two rows for partial: first row = total paid (green), total unpaid (red) with rowSpan 2, then remaining/paid per month
            const row1Cells = [
              {
                content: this.formatMontant(studentTotalPaidYear) || "0.000",
                styles: { fontSize: 8, fillColor: paidColor },
                rowSpan: 2,
              },
              {
                content:
                  this.formatMontant(studentTotalUnpaidYear) || "0.000",
                styles: { fontSize: 8, fillColor: color },
                rowSpan: 2,
              },
            ];
            for (let col = 1; col <= 9; col++) {
              const monthKey = monthKeysByCol[col - 1];
              if (hasPartialInMonth(monthKey)) {
                row1Cells.push({
                  content:
                    this.formatMontant(student.monthsUnpaid?.[monthKey]) ||
                    "0.000",
                  styles: { fontSize: 8, fillColor: color },
                });
              } else {
                row1Cells.push({
                  content: getMonthContent(monthKey),
                  styles: {
                    fontSize: 8,
                    fillColor: getMonthFillColor(monthKey),
                  },
                  rowSpan: 2,
                });
              }
            }
            const monthKeySept = monthKeysByCol[9];
            if (hasPartialInMonth(monthKeySept)) {
              row1Cells.push({
                content:
                  this.formatMontant(student.monthsUnpaid?.[monthKeySept]) ||
                  "0.000",
                styles: { fontSize: 8, fillColor: color },
              });
            } else {
              row1Cells.push({
                content: getMonthContent(monthKeySept),
                styles: {
                  fontSize: 8,
                  fillColor: getMonthFillColor(monthKeySept),
                },
                rowSpan: 2,
              });
            }
            // Registration: if partial, show unpaid in row1 and paid in row2; else rowSpan 2
            if (hasPartialInscription) {
              row1Cells.push({
                content: this.formatMontant(student.inscription) || "0.000",
                styles: { fontSize: 8, fillColor: color },
              });
            } else {
              row1Cells.push({
                content: this.getInscriptionContent(student),
                rowSpan: 2,
                styles: {
                  fontSize: 8,
                  fillColor: this.getInscriptionFillColor(
                    student,
                    paidColor,
                    color,
                    discountColor
                  ),
                },
              });
            }
            row1Cells.push(
              {
                content: student.monthly,
                styles: {
                  fillColor: isFullYearDiscount ? discountColor : "#ffffff",
                },
                rowSpan: 2,
              },
              { content: student.name, rowSpan: 2 },
              { content: studentId++, rowSpan: 2 }
            );

            const row2Cells = [];
            for (let col = 1; col <= 9; col++) {
              const monthKey = monthKeysByCol[col - 1];
              if (hasPartialInMonth(monthKey)) {
                row2Cells.push({
                  content:
                    this.formatMontant(student.monthsPartialPaid?.[monthKey]) ||
                    "0.000",
                  styles: { fontSize: 8, fillColor: "#fff9c4" },
                });
              }
            }
            if (hasPartialInMonth(monthKeysByCol[9])) {
              row2Cells.push({
                content:
                  this.formatMontant(
                    student.monthsPartialPaid?.[monthKeysByCol[9]]
                  ) || "0.000",
                styles: { fontSize: 8, fillColor: "#fff9c4" },
              });
            }
            // Registration: if partial, add paid amount in row2 (yellow)
            if (hasPartialInscription) {
              row2Cells.push({
                content:
                  this.formatMontant(student.inscriptionPaid) || "0.000",
                styles: { fontSize: 8, fillColor: "#fff9c4" },
              });
            }

            array.push(reverseIfNotArabic(row1Cells));
            array.push(reverseIfNotArabic(row2Cells));
          }

          // Get inscription total for monthly students only (unpaid and paid)
          const monthlyInscriptionTotal = monthlyStudents.reduce(
            (acc, student) => acc + (student.inscription || 0),
            0
          );
          const fullInscriptionMonthly =
            classroom.inscription != null && classroom.inscription !== false
              ? Number(classroom.inscription)
              : 0;
          const monthlyInscriptionPaidTotal = monthlyStudents.reduce(
            (acc, student) => {
              let paid = 0;
              // If inscriptionPaid > 0, use that value directly
              if (Number(student.inscriptionPaid) > 0) {
                paid = Number(student.inscriptionPaid);
              }
              // Else if inscription is 0 (fully paid) and classroom has inscription fee, count it as fully paid
              else if (
                Number(student.inscription) === 0 &&
                fullInscriptionMonthly > 0
              ) {
                paid = fullInscriptionMonthly;
              }
              return acc + paid;
            },
            0
          );

          // Footer: row 1 = total paid (green), row 2 = total unpaid (red), then total sum row
          // Derive footer totals from the same student rows we display so they always match
          const monthKeysForTotal = [6, 5, 4, 3, 2, 1, 12, 11, 10, 9];
          const summedTotalPaidFromStudents = monthlyStudents.reduce(
            (acc, s) => {
              const inscriptionPaidForStudent =
                Number(s.inscription) > 0
                  ? Number(s.inscriptionPaid) || 0
                  : Number(s.inscriptionPaid) || 0;
              const paidMonths = monthKeysForTotal.reduce(
                (a, m) =>
                  a +
                  (s.monthsPaid?.[m] || 0) +
                  (s.monthsPartialPaid?.[m] || 0),
                0
              );
              return acc + paidMonths + inscriptionPaidForStudent;
            },
            0
          );
          const summedTotalUnpaidFromStudents = monthlyStudents.reduce(
            (acc, s) =>
              acc +
              (s.montantTotalMonths || 0) +
              (Number(s.inscription) || 0),
            0
          );

          const totalPaidMonths =
            (classroom.monthsTotalPaid &&
              monthKeysForTotal.reduce(
                (acc, m) => acc + (classroom.monthsTotalPaid[m] || 0),
                0
              )) ||
            0;

          array.push(
            reverseIfNotArabic([
              {
                content: this.formatMontant(summedTotalPaidFromStudents) || "0.000",
                colSpan: 2,
                styles: {
                  fontStyle: "Bold",
                  fontSize: 10,
                  fillColor: paidColor,
                },
              },
              {
                content:
                  this.formatMontant(classroom.monthsTotalPaid?.[6]) || "0.000",
                styles: {
                  fontStyle: "Bold",
                  fontSize: 8,
                  fillColor: paidColor,
                },
              },
              {
                content:
                  this.formatMontant(classroom.monthsTotalPaid?.[5]) || "0.000",
                styles: {
                  fontStyle: "Bold",
                  fontSize: 8,
                  fillColor: paidColor,
                },
              },
              {
                content:
                  this.formatMontant(classroom.monthsTotalPaid?.[4]) || "0.000",
                styles: {
                  fontStyle: "Bold",
                  fontSize: 8,
                  fillColor: paidColor,
                },
              },
              {
                content:
                  this.formatMontant(classroom.monthsTotalPaid?.[3]) || "0.000",
                styles: {
                  fontStyle: "Bold",
                  fontSize: 8,
                  fillColor: paidColor,
                },
              },
              {
                content:
                  this.formatMontant(classroom.monthsTotalPaid?.[2]) || "0.000",
                styles: {
                  fontStyle: "Bold",
                  fontSize: 8,
                  fillColor: paidColor,
                },
              },
              {
                content:
                  this.formatMontant(classroom.monthsTotalPaid?.[1]) || "0.000",
                styles: {
                  fontStyle: "Bold",
                  fontSize: 8,
                  fillColor: paidColor,
                },
              },
              {
                content:
                  this.formatMontant(classroom.monthsTotalPaid?.[12]) ||
                  "0.000",
                styles: {
                  fontStyle: "Bold",
                  fontSize: 8,
                  fillColor: paidColor,
                },
              },
              {
                content:
                  this.formatMontant(classroom.monthsTotalPaid?.[11]) ||
                  "0.000",
                styles: {
                  fontStyle: "Bold",
                  fontSize: 8,
                  fillColor: paidColor,
                },
              },
              {
                content:
                  this.formatMontant(classroom.monthsTotalPaid?.[10]) ||
                  "0.000",
                styles: {
                  fontStyle: "Bold",
                  fontSize: 8,
                  fillColor: paidColor,
                },
              },
              {
                content:
                  this.formatMontant(classroom.monthsTotalPaid?.[9]) || "0.000",
                styles: {
                  fontStyle: "Bold",
                  fontSize: 8,
                  fillColor: paidColor,
                },
              },
              {
                content:
                  this.formatMontant(monthlyInscriptionPaidTotal) ||
                  "0.000",
                styles: {
                  fontStyle: "Bold",
                  fontSize: 10,
                  fillColor: paidColor,
                },
              },
              {
                content: t("paymentStatus.totalPaye"),
                colSpan: 3,
                styles: { fontSize: 11, lineWidth: 0.1 },
              },
            ]),
            reverseIfNotArabic([
              {
                content:
                  this.formatMontant(summedTotalUnpaidFromStudents) || "0.000",
                colSpan: 2,
                styles: {
                  fontSize: 10,
                  fontStyle: "Bold",
                  fillColor: color,
                },
              },
              {
                content:
                  this.formatMontant(classroom.monthsTotal[6]) || "0.000",
                styles: { fontStyle: "Bold", fontSize: 8, fillColor: color },
              },
              {
                content:
                  this.formatMontant(classroom.monthsTotal[5]) || "0.000",
                styles: { fontStyle: "Bold", fontSize: 8, fillColor: color },
              },
              {
                content:
                  this.formatMontant(classroom.monthsTotal[4]) || "0.000",
                styles: { fontStyle: "Bold", fontSize: 8, fillColor: color },
              },
              {
                content:
                  this.formatMontant(classroom.monthsTotal[3]) || "0.000",
                styles: { fontStyle: "Bold", fontSize: 8, fillColor: color },
              },
              {
                content:
                  this.formatMontant(classroom.monthsTotal[2]) || "0.000",
                styles: { fontStyle: "Bold", fontSize: 8, fillColor: color },
              },
              {
                content:
                  this.formatMontant(classroom.monthsTotal[1]) || "0.000",
                styles: { fontStyle: "Bold", fontSize: 8, fillColor: color },
              },
              {
                content:
                  this.formatMontant(classroom.monthsTotal[12]) || "0.000",
                styles: { fontStyle: "Bold", fontSize: 8, fillColor: color },
              },
              {
                content:
                  this.formatMontant(classroom.monthsTotal[11]) || "0.000",
                styles: { fontStyle: "Bold", fontSize: 8, fillColor: color },
              },
              {
                content:
                  this.formatMontant(classroom.monthsTotal[10]) || "0.000",
                styles: { fontStyle: "Bold", fontSize: 8, fillColor: color },
              },
              {
                content:
                  this.formatMontant(classroom.monthsTotal[9]) || "0.000",
                styles: { fontStyle: "Bold", fontSize: 8, fillColor: color },
              },
              {
                content:
                  this.formatMontant(monthlyInscriptionTotal) ||
                  "0.000",
                styles: {
                  fontStyle: "Bold",
                  fontSize: 10,
                  fillColor: color,
                },
              },
              {
                content: t("paymentStatus.totalNonPaye"),
                colSpan: 3,
                styles: { fontSize: 11, lineWidth: 0.1 },
              },
            ])
          );

          // fix table
          for (let row = 0; row < array.length; row++) {
            array[row] = array[row].filter((cell) => cell !== cellToDelete);
          }

          // Full width: Sept=col 11, Registration=col 12 (reversed for LTR so Registration shows before September); fixed cols (0,1,12,13,14,15) = 18+18+19+15+40+9; 10 month cols share the rest
          const monthlyFixedWidth = 18 + 18 + 19 + 15 + 40 + 9;
          const monthW = (fullTableWidth - monthlyFixedWidth) / 10;
          doc.autoTable({
            theme: "grid",
            tableWidth: fullTableWidth,
            styles: {
              font: "Amiri",
              halign: "center",
              fontSize: 9,
              valign: "middle",
              fontStyle: "normal",
              lineColor: "#363636",
              lineWidth: 0.1,
              cellPadding: {
                right: 0,
                left: 0,
                top: 1,
                bottom: 1,
              },
            },
            pageBreak: "avoid",
            body: array,
            columnStyles: reverseColumnStyles(
              {
                0: { cellWidth: 18 },
                1: { cellWidth: 18 },
                2: { cellWidth: monthW },
                3: { cellWidth: monthW },
                4: { cellWidth: monthW },
                5: { cellWidth: monthW },
                6: { cellWidth: monthW },
                7: { cellWidth: monthW },
                8: { cellWidth: monthW },
                9: { cellWidth: monthW },
                10: { cellWidth: monthW },
                11: { cellWidth: monthW },
                12: { cellWidth: 19 },
                13: { cellWidth: 15 },
                14: { cellWidth: 40 },
                15: { cellWidth: 9 },
              },
              16
            ),
            margin: {
              top: 35,
              left: tableMarginHorizontal,
              right: tableMarginHorizontal,
              bottom: LEGEND_FOOTER_HEIGHT_MM,
            },
            didDrawPage: function (data) {
              // Reseting top margin. The change will be reflected only after print the first page.
              data.settings.margin.top = 10;
            },
          });
        }

        // ==============================================
        // TABLE 2: TRANCHE STUDENTS
        // ==============================================
        if (trancheStudents.length > 0) {
          // Determine actual tranches for this classroom (from classroom.tranches)
          const classroomTranches = (classroom.tranches || [])
            .map((t) => t.trancheNumber)
            .sort((a, b) => b - a); // Descending: [4, 3, 2, 1] or [3, 2, 1], etc.

          const numTranches = classroomTranches.length;
          const numColumns = 2 + numTranches + 1 + 2; // Total paid, Total unpaid, Tranches, Registration, Name, ID

          // Build header row with actual tranches
          const headerRow = [
            {
              content: t("paymentStatus.totalPaye"),
              styles: { fontStyle: "Bold", fontSize: 8 },
            },
            {
              content: t("paymentStatus.totalNonPaye"),
              styles: { fontStyle: "Bold", fontSize: 8 },
            },
          ];
          classroomTranches.forEach((num) => {
            headerRow.push({
              content: t("scolaritePdf.installment", { number: num }),
              styles: { fontStyle: "Bold", fontSize: 9 },
            });
          });
          headerRow.push(
            {
              content: t("scolaritePdf.registration"),
              styles: { fontStyle: "Bold", fontSize: 9 },
            },
            {
              content: this.context.schoolarYearName,
              colSpan: 2,
              styles: { fontStyle: "Bold", fontSize: 9 },
            }
          );

          let array = [
            // Title row with class name
            reverseIfNotArabic([
              {
                content:
                  t("encaissement.installmentPayment") + " - " + classroom.name,
                colSpan: numColumns,
                styles: {
                  fontStyle: "Bold",
                  fontSize: 10,
                  fillColor: "#e8f4f8",
                },
              },
            ]),
            reverseIfNotArabic(headerRow),
          ];

          let arr = [];
          let studentId = 1;

          // Tranche columns = actual tranches for this classroom (descending)
          const trancheKeysByCol = classroomTranches;

          for (const student of trancheStudents) {
            // Calculate inscription paid for this student
            const studentInscriptionPaid =
              Number(student.inscription) > 0
                ? Number(student.inscriptionPaid) || 0
                : Number(student.inscriptionPaid) || 0;

            // Total paid for the year (tranches paid + partial paid + inscription paid)
            const studentTotalPaidYear =
              trancheKeysByCol.reduce(
                (acc, n) =>
                  acc +
                  (student.tranchesPaid?.[n] || 0) +
                  (student.tranchesPartialPaid?.[n] || 0),
                0
              ) + studentInscriptionPaid;

            const getTrancheContent = (trancheKey) => {
              const unpaid = this.formatMontant(
                student.tranchesUnpaid?.[trancheKey]
              );
              const paid = this.formatMontant(
                student.tranchesPaid?.[trancheKey]
              );
              // When partial we use two rows (remaining + paid), no combined text
              return unpaid || paid || "0.000";
            };

            // Blue: tranche cell only when paid + per-tranche discount (not global)
            const isTrancheDiscounted = (trancheKey) =>
              !!student.tranchesDiscounted?.[trancheKey];
            const getTrancheFillColor = (trancheKey) => {
              if (this.formatMontant(student.tranchesUnpaid?.[trancheKey]))
                return color;
              if (student.tranchesPartialPaid?.[trancheKey]) return "#fff9c4";
              if (this.formatMontant(student.tranchesPaid?.[trancheKey]))
                return !student.reductionGlobal &&
                  isTrancheDiscounted(trancheKey)
                  ? discountColor
                  : paidColor;
              return "#ffffff";
            };

            const hasPartialInTranche = (trancheKey) =>
              !!this.formatMontant(student.tranchesPartialPaid?.[trancheKey]);
            const hasPartialInscription =
              Number(student.inscription) > 0 &&
              Number(student.inscriptionPaid) > 0;
            const hasAnyPartial =
              trancheKeysByCol.some(hasPartialInTranche) || hasPartialInscription;

            // Total unpaid for the year (tranches unpaid + inscription unpaid)
            const studentTotalUnpaidYear =
              (student.montantTotalTranches || 0) + (Number(student.inscription) || 0);

            if (!hasAnyPartial) {
              // Single row: build cells for actual tranches only
              const rowCells = [
                {
                  content:
                    this.formatMontant(studentTotalPaidYear) || "0.000",
                  styles: { fontSize: 8, fillColor: paidColor },
                },
                {
                  content:
                    this.formatMontant(studentTotalUnpaidYear) || "0.000",
                  styles: { fontSize: 8, fillColor: color },
                },
              ];
              trancheKeysByCol.forEach((trancheKey) => {
                rowCells.push({
                  content: getTrancheContent(trancheKey),
                  styles: {
                    fontSize: 8,
                    fillColor: getTrancheFillColor(trancheKey),
                  },
                });
              });
              rowCells.push(
                {
                  content: this.getInscriptionContent(student),
                  styles: {
                    fontSize: 8,
                    fillColor: this.getInscriptionFillColor(
                      student,
                      paidColor,
                      color,
                      discountColor
                    ),
                  },
                },
                { content: student.name },
                { content: studentId++ }
              );
              array.push(reverseIfNotArabic(rowCells));
              continue;
            }

            // Two rows for partial: total paid (green), total unpaid (red) with rowSpan 2, then tranches
            const row1Cells = [
              {
                content: this.formatMontant(studentTotalPaidYear) || "0.000",
                styles: { fontSize: 8, fillColor: paidColor },
                rowSpan: 2,
              },
              {
                content:
                  this.formatMontant(studentTotalUnpaidYear) || "0.000",
                styles: { fontSize: 8, fillColor: color },
                rowSpan: 2,
              },
            ];
            trancheKeysByCol.forEach((trancheKey) => {
              if (hasPartialInTranche(trancheKey)) {
                row1Cells.push({
                  content:
                    this.formatMontant(student.tranchesUnpaid?.[trancheKey]) ||
                    "0.000",
                  styles: { fontSize: 8, fillColor: color },
                });
              } else {
                row1Cells.push({
                  content: getTrancheContent(trancheKey),
                  styles: {
                    fontSize: 8,
                    fillColor: getTrancheFillColor(trancheKey),
                  },
                  rowSpan: 2,
                });
              }
            });
            // Registration: if partial, show unpaid in row1 and paid in row2; else rowSpan 2
            if (hasPartialInscription) {
              row1Cells.push({
                content: this.formatMontant(student.inscription) || "0.000",
                styles: { fontSize: 8, fillColor: color },
              });
            } else {
              row1Cells.push({
                content: this.getInscriptionContent(student),
                rowSpan: 2,
                styles: {
                  fontSize: 8,
                  fillColor: this.getInscriptionFillColor(
                    student,
                    paidColor,
                    color,
                    discountColor
                  ),
                },
              });
            }
            row1Cells.push(
              { content: student.name, rowSpan: 2 },
              { content: studentId++, rowSpan: 2 }
            );

            const row2Cells = [];
            trancheKeysByCol.forEach((trancheKey) => {
              if (hasPartialInTranche(trancheKey)) {
                row2Cells.push({
                  content:
                    this.formatMontant(
                      student.tranchesPartialPaid?.[trancheKey]
                    ) || "0.000",
                  styles: { fontSize: 8, fillColor: "#fff9c4" },
                });
              }
            });
            // Registration: if partial, add paid amount in row2 (yellow)
            if (hasPartialInscription) {
              row2Cells.push({
                content:
                  this.formatMontant(student.inscriptionPaid) || "0.000",
                styles: { fontSize: 8, fillColor: "#fff9c4" },
              });
            }

            array.push(reverseIfNotArabic(row1Cells));
            array.push(reverseIfNotArabic(row2Cells));
          }

          // Get inscription total for tranche students only (unpaid and paid)
          const trancheInscriptionTotal = trancheStudents.reduce(
            (acc, student) => acc + (student.inscription || 0),
            0
          );
          const fullInscriptionTranche =
            classroom.inscription != null && classroom.inscription !== false
              ? Number(classroom.inscription)
              : 0;
          const trancheInscriptionPaidTotal = trancheStudents.reduce(
            (acc, student) => {
              let paid = 0;
              // If inscriptionPaid > 0, use that value directly
              if (Number(student.inscriptionPaid) > 0) {
                paid = Number(student.inscriptionPaid);
              }
              // Else if inscription is 0 (fully paid) and classroom has inscription fee, count it as fully paid
              else if (
                Number(student.inscription) === 0 &&
                fullInscriptionTranche > 0
              ) {
                paid = fullInscriptionTranche;
              }
              return acc + paid;
            },
            0
          );

          // Derive footer totals from the same student rows we display so they always match
          const summedTotalPaidTranchesFromStudents = trancheStudents.reduce(
            (acc, s) => {
              const inscriptionPaidForStudent =
                Number(s.inscription) > 0
                  ? Number(s.inscriptionPaid) || 0
                  : Number(s.inscriptionPaid) || 0;
              const paidTranches = classroomTranches.reduce(
                (a, n) =>
                  a +
                  (s.tranchesPaid?.[n] || 0) +
                  (s.tranchesPartialPaid?.[n] || 0),
                0
              );
              return acc + paidTranches + inscriptionPaidForStudent;
            },
            0
          );
          const summedTotalUnpaidTranchesFromStudents = trancheStudents.reduce(
            (acc, s) =>
              acc +
              (s.montantTotalTranches || 0) +
              (Number(s.inscription) || 0),
            0
          );

          // Footer: row 1 = total paid (green), row 2 = total unpaid (red)
          const totalPaidTranches =
            (classroom.tranchesTotalPaid &&
              classroomTranches.reduce(
                (acc, n) => acc + (classroom.tranchesTotalPaid[n] || 0),
                0
              )) ||
            0;

          // Build footer row 1 (paid) with actual tranches
          const footerPaidRow = [
            {
              content: this.formatMontant(summedTotalPaidTranchesFromStudents) || "0.000",
              colSpan: 2,
              styles: {
                fontSize: 10,
                fontStyle: "Bold",
                fillColor: paidColor,
              },
            },
          ];
          classroomTranches.forEach((num) => {
            footerPaidRow.push({
              content:
                this.formatMontant(classroom.tranchesTotalPaid?.[num]) ||
                "0.000",
              styles: {
                fontStyle: "Bold",
                fontSize: 8,
                fillColor: paidColor,
              },
            });
          });
          footerPaidRow.push(
            {
              content:
                this.formatMontant(trancheInscriptionPaidTotal) || "0.000",
              styles: {
                fontStyle: "Bold",
                fontSize: 10,
                fillColor: paidColor,
              },
            },
            {
              content: t("paymentStatus.totalPaye"),
              colSpan: 2,
              styles: { fontSize: 11, lineWidth: 0.1 },
            }
          );

          // Build footer row 2 (unpaid) with actual tranches
          const footerUnpaidRow = [
            {
              content:
                this.formatMontant(summedTotalUnpaidTranchesFromStudents) || "0.000",
              colSpan: 2,
              styles: {
                fontSize: 10,
                fontStyle: "Bold",
                fillColor: color,
              },
            },
          ];
          classroomTranches.forEach((num) => {
            footerUnpaidRow.push({
              content:
                this.formatMontant(classroom.tranchesTotal[num]) || "0.000",
              styles: { fontStyle: "Bold", fontSize: 8, fillColor: color },
            });
          });
          footerUnpaidRow.push(
            {
              content: this.formatMontant(trancheInscriptionTotal) || "0.000",
              styles: {
                fontStyle: "Bold",
                fontSize: 10,
                fillColor: color,
              },
            },
            {
              content: t("paymentStatus.totalNonPaye"),
              colSpan: 2,
              styles: { fontSize: 11, lineWidth: 0.1 },
            }
          );

          array.push(reverseIfNotArabic(footerPaidRow));
          array.push(reverseIfNotArabic(footerUnpaidRow));

          // fix table
          for (let row = 0; row < array.length; row++) {
            array[row] = array[row].filter((cell) => cell !== cellToDelete);
          }

          const trancheFixedWidth = 18 + 18 + 40 + 9 + 19;
          const trancheW = (fullTableWidth - trancheFixedWidth) / numTranches;

          // Build columnStyles dynamically based on number of tranches
          const columnStyles = {
            0: { cellWidth: 18 }, // Total paid
            1: { cellWidth: 18 }, // Total unpaid
          };
          for (let i = 0; i < numTranches; i++) {
            columnStyles[2 + i] = { cellWidth: trancheW };
          }
          columnStyles[2 + numTranches] = { cellWidth: 19 }; // Inscription
          columnStyles[3 + numTranches] = { cellWidth: 40 }; // Name
          columnStyles[4 + numTranches] = { cellWidth: 9 }; // ID

          doc.autoTable({
            theme: "grid",
            tableWidth: fullTableWidth,
            styles: {
              font: "Amiri",
              halign: "center",
              fontSize: 9,
              valign: "middle",
              fontStyle: "normal",
              lineColor: "#363636",
              lineWidth: 0.1,
              cellPadding: {
                right: 0,
                left: 0,
                top: 1,
                bottom: 1,
              },
            },
            pageBreak: "avoid",
            body: array,
            columnStyles: reverseColumnStyles(columnStyles, numColumns),
            margin: {
              top: 35,
              left: tableMarginHorizontal,
              right: tableMarginHorizontal,
              bottom: LEGEND_FOOTER_HEIGHT_MM,
            },
            didDrawPage: function (data) {
              // Reseting top margin. The change will be reflected only after print the first page.
              data.settings.margin.top = 10;
            },
          });
        }
      }

      doc.addPage();
    }

    // Month names for summary table
    const summaryMonthNames = [
      t("months.june"),
      t("months.may"),
      t("months.april"),
      t("months.march"),
      t("months.february"),
      t("months.january"),
      t("months.december"),
      t("months.november"),
      t("months.october"),
      t("months.september"),
    ];

    // ==============================================
    // SUMMARY TABLE 1: MONTHLY (ALL LEVELS)
    // ==============================================
    const monthlySummary = [
      // Title row
      reverseIfNotArabic([
        {
          content: t("encaissement.monthlyPayment"),
          colSpan: 13,
          styles: { fontStyle: "Bold", fontSize: 10, fillColor: "#e8f4f8" },
        },
      ]),
      // Header row (internal order: ... Oct, Sept, Registration, Levels → LTR shows Registration before Sept)
      reverseIfNotArabic([
        {
          content: t("scolaritePdf.schooling"),
          styles: { fontStyle: "Bold", fontSize: 10 },
        },
        {
          content: summaryMonthNames[0],
          styles: { fontStyle: "Bold", fontSize: 10 },
        },
        {
          content: summaryMonthNames[1],
          styles: { fontStyle: "Bold", fontSize: 10 },
        },
        {
          content: summaryMonthNames[2],
          styles: { fontStyle: "Bold", fontSize: 10 },
        },
        {
          content: summaryMonthNames[3],
          styles: { fontStyle: "Bold", fontSize: 10 },
        },
        {
          content: summaryMonthNames[4],
          styles: { fontStyle: "Bold", fontSize: 10 },
        },
        {
          content: summaryMonthNames[5],
          styles: { fontStyle: "Bold", fontSize: 10 },
        },
        {
          content: summaryMonthNames[6],
          styles: { fontStyle: "Bold", fontSize: 10 },
        },
        {
          content: summaryMonthNames[7],
          styles: { fontStyle: "Bold", fontSize: 10 },
        },
        {
          content: summaryMonthNames[8],
          styles: { fontStyle: "Bold", fontSize: 10 },
        },
        {
          content: summaryMonthNames[9],
          styles: { fontStyle: "Bold", fontSize: 10 },
        },
        {
          content: t("scolaritePdf.registration"),
          styles: { fontStyle: "Bold", fontSize: 10 },
        },
        {
          content: t("scolaritePdf.levels"),
          styles: { fontStyle: "Bold", fontSize: 10 },
        },
      ]),
    ];

    try {
      const logoX = isArabic ? 10 : pdf_width - 30; // Left for Arabic, right for others
      doc.addImage(imgData, "JPEG", logoX, 8, 20, 20);
    } catch (error) {
      console.log(error);
    }

    doc.setFont("Amiri", "normal");
    doc.setFontSize(10);

    const options = {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    };

    doc.text(
      new Date().toLocaleDateString(locale, options),
      isArabic ? pdf_width - 17 : 17,
      8,
      {
        align: isArabic ? "right" : "left",
      }
    );

    doc.setFontSize(15);
    doc.setFont("Amiri", "Bold");
    doc.setTextColor("#505050");
    let text =
      t("encaissement.schoolingStateOfUnpaid") +
      " - " +
      t("scolaritePdf.allLevels");
    doc.text(text, pdf_width / 2, 22, {
      align: "center",
    });

    const paidColor = "#c8e6c9"; // green for paid
    const color = "#fcaa97"; // red for unpaid

    for (const level of impayeData.levels) {
      // Calculate total paid for this level
      const levelTotalPaid = level.monthsTotalPaid
        ? [6, 5, 4, 3, 2, 1, 12, 11, 10, 9].reduce(
            (acc, m) => acc + (level.monthsTotalPaid[m] || 0),
            0
          )
        : 0;

      // Row 1: Total paid (green) + paid amounts per month (green)
      monthlySummary.push(
        reverseIfNotArabic([
          {
            content: this.formatMontant(levelTotalPaid) || "0.000",
            styles: { fontSize: 8, fillColor: paidColor, fontStyle: "Bold" },
          },
          {
            content: this.formatMontant(level.monthsTotalPaid?.[6]) || "0.000",
            styles: { fontSize: 8, fillColor: paidColor },
          },
          {
            content: this.formatMontant(level.monthsTotalPaid?.[5]) || "0.000",
            styles: { fontSize: 8, fillColor: paidColor },
          },
          {
            content: this.formatMontant(level.monthsTotalPaid?.[4]) || "0.000",
            styles: { fontSize: 8, fillColor: paidColor },
          },
          {
            content: this.formatMontant(level.monthsTotalPaid?.[3]) || "0.000",
            styles: { fontSize: 8, fillColor: paidColor },
          },
          {
            content: this.formatMontant(level.monthsTotalPaid?.[2]) || "0.000",
            styles: { fontSize: 8, fillColor: paidColor },
          },
          {
            content: this.formatMontant(level.monthsTotalPaid?.[1]) || "0.000",
            styles: { fontSize: 8, fillColor: paidColor },
          },
          {
            content: this.formatMontant(level.monthsTotalPaid?.[12]) || "0.000",
            styles: { fontSize: 8, fillColor: paidColor },
          },
          {
            content: this.formatMontant(level.monthsTotalPaid?.[11]) || "0.000",
            styles: { fontSize: 8, fillColor: paidColor },
          },
          {
            content: this.formatMontant(level.monthsTotalPaid?.[10]) || "0.000",
            styles: { fontSize: 8, fillColor: paidColor },
          },
          {
            content: this.formatMontant(level.monthsTotalPaid?.[9]) || "0.000",
            styles: { fontSize: 8, fillColor: paidColor },
          },
          {
            content:
              this.formatMontant(level.montantTotalInscriptionPaid) || "0.000",
            styles: { fontSize: 8, fillColor: paidColor },
          },
          {
            content: levels[String(level.level)] || level.level,
            styles: { fontSize: 9, fontStyle: "Bold" },
            rowSpan: 2,
          },
        ])
      );

      // Row 2: Total unpaid (red) + unpaid amounts per month (red)
      monthlySummary.push(
        reverseIfNotArabic([
          {
            content: this.formatMontant(level.montantTotalMonths) || "0.000",
            styles: { fontSize: 8, fillColor: color, fontStyle: "Bold" },
          },
          {
            content: this.formatMontant(level.monthsTotal[6]) || "0.000",
            styles: { fontSize: 8, fillColor: color },
          },
          {
            content: this.formatMontant(level.monthsTotal[5]) || "0.000",
            styles: { fontSize: 8, fillColor: color },
          },
          {
            content: this.formatMontant(level.monthsTotal[4]) || "0.000",
            styles: { fontSize: 8, fillColor: color },
          },
          {
            content: this.formatMontant(level.monthsTotal[3]) || "0.000",
            styles: { fontSize: 8, fillColor: color },
          },
          {
            content: this.formatMontant(level.monthsTotal[2]) || "0.000",
            styles: { fontSize: 8, fillColor: color },
          },
          {
            content: this.formatMontant(level.monthsTotal[1]) || "0.000",
            styles: { fontSize: 8, fillColor: color },
          },
          {
            content: this.formatMontant(level.monthsTotal[12]) || "0.000",
            styles: { fontSize: 8, fillColor: color },
          },
          {
            content: this.formatMontant(level.monthsTotal[11]) || "0.000",
            styles: { fontSize: 8, fillColor: color },
          },
          {
            content: this.formatMontant(level.monthsTotal[10]) || "0.000",
            styles: { fontSize: 8, fillColor: color },
          },
          {
            content: this.formatMontant(level.monthsTotal[9]) || "0.000",
            styles: { fontSize: 8, fillColor: color },
          },
          {
            content:
              this.formatMontant(level.montantTotalInscription) || "0.000",
            styles: { fontSize: 8, fillColor: color },
          },
        ])
      );
    }

    // Calculate overall total paid
    const overallTotalPaid = impayeData.monthsTotalPaid
      ? [6, 5, 4, 3, 2, 1, 12, 11, 10, 9].reduce(
          (acc, m) => acc + (impayeData.monthsTotalPaid[m] || 0),
          0
        )
      : 0;

    monthlySummary.push(
      // Row 1: Overall total paid (green) + paid amounts per month (green)
      reverseIfNotArabic([
        {
          content: this.formatMontant(overallTotalPaid) || "0.000",
          styles: { fontStyle: "Bold", fontSize: 10, fillColor: paidColor },
        },
        {
          content:
            this.formatMontant(impayeData.monthsTotalPaid?.[6]) || "0.000",
          styles: { fontStyle: "Bold", fontSize: 8, fillColor: paidColor },
        },
        {
          content:
            this.formatMontant(impayeData.monthsTotalPaid?.[5]) || "0.000",
          styles: { fontStyle: "Bold", fontSize: 8, fillColor: paidColor },
        },
        {
          content:
            this.formatMontant(impayeData.monthsTotalPaid?.[4]) || "0.000",
          styles: { fontStyle: "Bold", fontSize: 8, fillColor: paidColor },
        },
        {
          content:
            this.formatMontant(impayeData.monthsTotalPaid?.[3]) || "0.000",
          styles: { fontStyle: "Bold", fontSize: 8, fillColor: paidColor },
        },
        {
          content:
            this.formatMontant(impayeData.monthsTotalPaid?.[2]) || "0.000",
          styles: { fontStyle: "Bold", fontSize: 8, fillColor: paidColor },
        },
        {
          content:
            this.formatMontant(impayeData.monthsTotalPaid?.[1]) || "0.000",
          styles: { fontStyle: "Bold", fontSize: 8, fillColor: paidColor },
        },
        {
          content:
            this.formatMontant(impayeData.monthsTotalPaid?.[12]) || "0.000",
          styles: { fontStyle: "Bold", fontSize: 8, fillColor: paidColor },
        },
        {
          content:
            this.formatMontant(impayeData.monthsTotalPaid?.[11]) || "0.000",
          styles: { fontStyle: "Bold", fontSize: 8, fillColor: paidColor },
        },
        {
          content:
            this.formatMontant(impayeData.monthsTotalPaid?.[10]) || "0.000",
          styles: { fontStyle: "Bold", fontSize: 8, fillColor: paidColor },
        },
        {
          content:
            this.formatMontant(impayeData.monthsTotalPaid?.[9]) || "0.000",
          styles: { fontStyle: "Bold", fontSize: 8, fillColor: paidColor },
        },
        {
          content:
            this.formatMontant(impayeData.montantTotalInscriptionPaid) ||
            "0.000",
          styles: { fontStyle: "Bold", fontSize: 10, fillColor: paidColor },
        },
        {
          content: t("paymentStatus.totalPaye"),
          styles: { fontSize: 11, lineWidth: 0.1 },
        },
      ]),
      // Row 2: Overall total unpaid (red) + unpaid amounts per month (red)
      reverseIfNotArabic([
        {
          content: this.formatMontant(impayeData.montantTotalMonths) || "0.000",
          styles: { fontStyle: "Bold", fontSize: 10, fillColor: color },
        },
        {
          content: this.formatMontant(impayeData.monthsTotal[6]) || "0.000",
          styles: { fontStyle: "Bold", fontSize: 8, fillColor: color },
        },
        {
          content: this.formatMontant(impayeData.monthsTotal[5]) || "0.000",
          styles: { fontStyle: "Bold", fontSize: 8, fillColor: color },
        },
        {
          content: this.formatMontant(impayeData.monthsTotal[4]) || "0.000",
          styles: { fontStyle: "Bold", fontSize: 8, fillColor: color },
        },
        {
          content: this.formatMontant(impayeData.monthsTotal[3]) || "0.000",
          styles: { fontStyle: "Bold", fontSize: 8, fillColor: color },
        },
        {
          content: this.formatMontant(impayeData.monthsTotal[2]) || "0.000",
          styles: { fontStyle: "Bold", fontSize: 8, fillColor: color },
        },
        {
          content: this.formatMontant(impayeData.monthsTotal[1]) || "0.000",
          styles: { fontStyle: "Bold", fontSize: 8, fillColor: color },
        },
        {
          content: this.formatMontant(impayeData.monthsTotal[12]) || "0.000",
          styles: { fontStyle: "Bold", fontSize: 8, fillColor: color },
        },
        {
          content: this.formatMontant(impayeData.monthsTotal[11]) || "0.000",
          styles: { fontStyle: "Bold", fontSize: 8, fillColor: color },
        },
        {
          content: this.formatMontant(impayeData.monthsTotal[10]) || "0.000",
          styles: { fontStyle: "Bold", fontSize: 8, fillColor: color },
        },
        {
          content: this.formatMontant(impayeData.monthsTotal[9]) || "0.000",
          styles: { fontStyle: "Bold", fontSize: 8, fillColor: color },
        },
        {
          content:
            this.formatMontant(impayeData.montantTotalInscription) || "0.000",
          styles: { fontStyle: "Bold", fontSize: 8, fillColor: color },
        },
        {
          content: t("paymentStatus.totalNonPaye"),
          styles: { fontSize: 11, lineWidth: 0.1 },
        },
      ])
    );

    // fix table
    for (let row = 0; row < monthlySummary.length; row++) {
      monthlySummary[row] = monthlySummary[row].filter(
        (cell) => cell !== cellToDelete
      );
    }

    // Summary monthly: first col 25, Registration 22, Level 22; 10 month cols share the rest
    const scolaritySummaryMonthlyFixedWidth = 25 + 22 + 22;
    const scolaritySummaryMonthW =
      (fullTableWidth - scolaritySummaryMonthlyFixedWidth) / 10;
    doc.autoTable({
      theme: "grid",
      tableWidth: fullTableWidth,
      styles: {
        font: "Amiri",
        halign: "center",
        fontSize: 9,
        valign: "middle",
        fontStyle: "normal",
        lineColor: "#363636",
        lineWidth: 0.1,
        cellPadding: {
          right: 0,
          left: 0,
          top: 1.25,
          bottom: 1.25,
        },
      },
      pageBreak: "avoid",
      body: monthlySummary,
      columnStyles: reverseColumnStyles(
        {
          0: { cellWidth: 25 },
          1: { cellWidth: scolaritySummaryMonthW },
          2: { cellWidth: scolaritySummaryMonthW },
          3: { cellWidth: scolaritySummaryMonthW },
          4: { cellWidth: scolaritySummaryMonthW },
          5: { cellWidth: scolaritySummaryMonthW },
          6: { cellWidth: scolaritySummaryMonthW },
          7: { cellWidth: scolaritySummaryMonthW },
          8: { cellWidth: scolaritySummaryMonthW },
          9: { cellWidth: scolaritySummaryMonthW },
          10: { cellWidth: 22 },
          11: { cellWidth: scolaritySummaryMonthW },
          12: { cellWidth: 22 },
        },
        13
      ),
      margin: {
        top: 35,
        left: tableMarginHorizontal,
        right: tableMarginHorizontal,
        bottom: LEGEND_FOOTER_HEIGHT_MM,
      },
      didDrawPage: function (data) {
        // Reseting top margin. The change will be reflected only after print the first page.
        data.settings.margin.top = 10;
      },
    });

    // ==============================================
    // SUMMARY TABLE 2: TRANCHES (ALL LEVELS)
    // ==============================================
    const trancheSummary = [
      // Title row
      reverseIfNotArabic([
        {
          content: t("encaissement.installmentPayment"),
          colSpan: 7,
          styles: { fontStyle: "Bold", fontSize: 10, fillColor: "#e8f4f8" },
        },
      ]),
      // Header row (internal order: ... T1, Registration, Levels → LTR shows Registration before tranches)
      reverseIfNotArabic([
        {
          content: t("scolaritePdf.schooling"),
          styles: { fontStyle: "Bold", fontSize: 10 },
        },
        {
          content: t("scolaritePdf.installment", { number: 4 }),
          styles: { fontStyle: "Bold", fontSize: 10 },
        },
        {
          content: t("scolaritePdf.installment", { number: 3 }),
          styles: { fontStyle: "Bold", fontSize: 10 },
        },
        {
          content: t("scolaritePdf.installment", { number: 2 }),
          styles: { fontStyle: "Bold", fontSize: 10 },
        },
        {
          content: t("scolaritePdf.installment", { number: 1 }),
          styles: { fontStyle: "Bold", fontSize: 10 },
        },
        {
          content: t("scolaritePdf.registration"),
          styles: { fontStyle: "Bold", fontSize: 10 },
        },
        {
          content: t("scolaritePdf.levels"),
          styles: { fontStyle: "Bold", fontSize: 10 },
        },
      ]),
    ];

    for (const level of impayeData.levels) {
      // Calculate total paid for this level (tranches)
      const levelTotalPaidTranches = level.tranchesTotalPaid
        ? [4, 3, 2, 1].reduce(
            (acc, n) => acc + (level.tranchesTotalPaid[n] || 0),
            0
          )
        : 0;

      // Row 1: Total paid (green) + paid amounts per tranche (green)
      trancheSummary.push(
        reverseIfNotArabic([
          {
            content: this.formatMontant(levelTotalPaidTranches) || "0.000",
            styles: { fontSize: 8, fillColor: paidColor, fontStyle: "Bold" },
          },
          {
            content:
              this.formatMontant(level.tranchesTotalPaid?.[4]) || "0.000",
            styles: { fontSize: 8, fillColor: paidColor },
          },
          {
            content:
              this.formatMontant(level.tranchesTotalPaid?.[3]) || "0.000",
            styles: { fontSize: 8, fillColor: paidColor },
          },
          {
            content:
              this.formatMontant(level.tranchesTotalPaid?.[2]) || "0.000",
            styles: { fontSize: 8, fillColor: paidColor },
          },
          {
            content:
              this.formatMontant(level.tranchesTotalPaid?.[1]) || "0.000",
            styles: { fontSize: 8, fillColor: paidColor },
          },
          {
            content:
              this.formatMontant(level.montantTotalInscriptionPaid) || "0.000",
            styles: { fontSize: 8, fillColor: paidColor },
          },
          {
            content: levels[String(level.level)] || level.level,
            styles: { fontSize: 9, fontStyle: "Bold" },
            rowSpan: 2,
          },
        ])
      );

      // Row 2: Total unpaid (red) + unpaid amounts per tranche (red)
      trancheSummary.push(
        reverseIfNotArabic([
          {
            content: this.formatMontant(level.montantTotalTranches) || "0.000",
            styles: { fontSize: 8, fillColor: color, fontStyle: "Bold" },
          },
          {
            content: this.formatMontant(level.tranchesTotal?.[4]) || "0.000",
            styles: { fontSize: 8, fillColor: color },
          },
          {
            content: this.formatMontant(level.tranchesTotal?.[3]) || "0.000",
            styles: { fontSize: 8, fillColor: color },
          },
          {
            content: this.formatMontant(level.tranchesTotal?.[2]) || "0.000",
            styles: { fontSize: 8, fillColor: color },
          },
          {
            content: this.formatMontant(level.tranchesTotal?.[1]) || "0.000",
            styles: { fontSize: 8, fillColor: color },
          },
          {
            content:
              this.formatMontant(level.montantTotalInscription) || "0.000",
            styles: { fontSize: 8, fillColor: color },
          },
        ])
      );
    }

    // Calculate overall total paid (tranches)
    const overallTotalPaidTranches = impayeData.tranchesTotalPaid
      ? [4, 3, 2, 1].reduce(
          (acc, n) => acc + (impayeData.tranchesTotalPaid[n] || 0),
          0
        )
      : 0;

    trancheSummary.push(
      // Row 1: Overall total paid (green) + paid amounts per tranche (green)
      reverseIfNotArabic([
        {
          content: this.formatMontant(overallTotalPaidTranches) || "0.000",
          styles: { fontStyle: "Bold", fontSize: 10, fillColor: paidColor },
        },
        {
          content:
            this.formatMontant(impayeData.tranchesTotalPaid?.[4]) || "0.000",
          styles: { fontStyle: "Bold", fontSize: 8, fillColor: paidColor },
        },
        {
          content:
            this.formatMontant(impayeData.tranchesTotalPaid?.[3]) || "0.000",
          styles: { fontStyle: "Bold", fontSize: 8, fillColor: paidColor },
        },
        {
          content:
            this.formatMontant(impayeData.tranchesTotalPaid?.[2]) || "0.000",
          styles: { fontStyle: "Bold", fontSize: 8, fillColor: paidColor },
        },
        {
          content:
            this.formatMontant(impayeData.tranchesTotalPaid?.[1]) || "0.000",
          styles: { fontStyle: "Bold", fontSize: 8, fillColor: paidColor },
        },
        {
          content:
            this.formatMontant(impayeData.montantTotalInscriptionPaid) ||
            "0.000",
          styles: { fontStyle: "Bold", fontSize: 10, fillColor: paidColor },
        },
        {
          content: t("paymentStatus.totalPaye"),
          styles: { fontSize: 11, lineWidth: 0.1 },
        },
      ]),
      // Row 2: Overall total unpaid (red) + unpaid amounts per tranche (red)
      reverseIfNotArabic([
        {
          content:
            this.formatMontant(impayeData.montantTotalTranches) || "0.000",
          styles: { fontStyle: "Bold", fontSize: 10, fillColor: color },
        },
        {
          content: this.formatMontant(impayeData.tranchesTotal?.[4]) || "0.000",
          styles: { fontStyle: "Bold", fontSize: 8, fillColor: color },
        },
        {
          content: this.formatMontant(impayeData.tranchesTotal?.[3]) || "0.000",
          styles: { fontStyle: "Bold", fontSize: 8, fillColor: color },
        },
        {
          content: this.formatMontant(impayeData.tranchesTotal?.[2]) || "0.000",
          styles: { fontStyle: "Bold", fontSize: 8, fillColor: color },
        },
        {
          content: this.formatMontant(impayeData.tranchesTotal?.[1]) || "0.000",
          styles: { fontStyle: "Bold", fontSize: 8, fillColor: color },
        },
        {
          content:
            this.formatMontant(impayeData.montantTotalInscription) || "0.000",
          styles: { fontStyle: "Bold", fontSize: 10, fillColor: color },
        },
        {
          content: t("paymentStatus.totalNonPaye"),
          styles: { fontSize: 11, lineWidth: 0.1 },
        },
      ])
    );
    for (let row = 0; row < trancheSummary.length; row++) {
      trancheSummary[row] = trancheSummary[row].filter(
        (cell) => cell !== cellToDelete
      );
    }

    const trancheStartY =
      doc.lastAutoTable && doc.lastAutoTable.finalY
        ? doc.lastAutoTable.finalY + 8
        : 35;

    // Summary tranche: same full width (first 25, Level 40, Registration 22; 4 tranche cols share the rest)
    const scolaritySummaryTrancheFixedWidth = 25 + 40 + 22;
    const summaryTrancheW =
      (fullTableWidth - scolaritySummaryTrancheFixedWidth) / 4;

    doc.autoTable({
      theme: "grid",
      tableWidth: fullTableWidth,
      styles: {
        font: "Amiri",
        halign: "center",
        fontSize: 9,
        valign: "middle",
        fontStyle: "normal",
        lineColor: "#363636",
        lineWidth: 0.1,
        cellPadding: {
          right: 0,
          left: 0,
          top: 1.25,
          bottom: 1.25,
        },
      },
      pageBreak: "avoid",
      startY: trancheStartY,
      body: trancheSummary,
      columnStyles: reverseColumnStyles(
        {
          0: { cellWidth: 25 }, // Total
          1: { cellWidth: summaryTrancheW }, // Tranche 4
          2: { cellWidth: summaryTrancheW }, // Tranche 3
          3: { cellWidth: summaryTrancheW }, // Tranche 2
          4: { cellWidth: summaryTrancheW }, // Tranche 1
          5: { cellWidth: 40 }, // Level
          6: { cellWidth: 22 }, // Inscription
        },
        7
      ),
      margin: {
        left: tableMarginHorizontal,
        right: tableMarginHorizontal,
        bottom: LEGEND_FOOTER_HEIGHT_MM,
      },
    });

    //page numbering and legend on each page
    const pages = doc.internal.getNumberOfPages();
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;
    doc.setFontSize(8); //Optional

    for (let j = 1; j < pages + 1; j++) {
      doc.setPage(j);
      this.drawLegendOnPage(doc, pageWidth, pageHeight, t);
      let horizontalPos = pageWidth / 2;
      let verticalPos = pageHeight - 10;
      const pageText = t("pdf.pageNumber", {
        current: j,
        total: pages,
      });
      doc.text(pageText, horizontalPos, verticalPos, {
        align: "center",
      });
    }

    this.context.pdfLoading = false;
    const fileName = t("encaissement.schoolingStateOfUnpaid") + ".pdf";
    doc.save(fileName);
  }

  async generatePdfActivites() {
    if (!(await this.context.packHasBtnAccess("pEtatImpAct"))) return;

    this.context.pdfLoading = true;

    // Get locale and translation function
    const locale = this.context.$i18n.locale;
    const t = this.context.$i18n.t.bind(this.context.$i18n);
    const isArabic = locale === "ar-AR";
    // Original table order was for Arabic (RTL), so reverse for non-Arabic languages
    const reverseIfNotArabic = (arr) =>
      !isArabic ? arr.slice().reverse() : arr;
    // Reverse column styles to match reversed column order for non-Arabic languages
    const reverseColumnStyles = (styles, numColumns) => {
      if (isArabic) return styles;
      const reversed = {};
      for (let i = 0; i < numColumns; i++) {
        reversed[i] = styles[numColumns - 1 - i];
      }
      return reversed;
    };

    let imgData;
    let arrayOfImages = this.context.settings.image;
    for (let i = 0; i < arrayOfImages.length; i++) {
      if (this.context.settings.activeBuilding === arrayOfImages[i].db) {
        imgData = new Image();
        imgData.src = arrayOfImages[i].logo;
      }
    }

    let impayeData;

    await apiClient
      .get("/payment/etatImpaye/activity")
      .then((res) => (impayeData = res.data))
      .catch((e) => {
        console.error(e);
        this.context.$message.error(this.context.$i18n.t("error.error"));
      });

    // Get country code from context
    const activeBuilding = this.context.user.building.find(
      (el) => el.dbName == this.context.settings.activeBuilding
    );
    const countryCode = activeBuilding?.countryCode || "TN";

    // Get all levels using LevelConstants helper
    const levels = getAllLevels(t, countryCode);

    const doc = new JsPDF({
      orientation: "p",
      unit: "mm",
      format: "a3",
    });

    const pdf_width = doc.internal.pageSize.getWidth();
    const tableMarginHorizontalActivity = 10;
    const fullTableWidthActivity =
      pdf_width - tableMarginHorizontalActivity * 2;

    const cellToDelete = {};

    for (const level of impayeData.levels) {
      if (level.activites.length === 0) continue;
      try {
        const logoX = isArabic ? 10 : pdf_width - 30; // Left for Arabic, right for others
        doc.addImage(imgData, "JPEG", logoX, 8, 20, 20);
      } catch (error) {
        console.log(error);
      }

      doc.setFont("Amiri", "normal");
      doc.setFontSize(10);

      const options = {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
      };

      doc.text(
        new Date().toLocaleDateString(locale, options),
        isArabic ? pdf_width - 17 : 17,
        8,
        {
          align: isArabic ? "right" : "left",
        }
      );

      doc.text(this.context.schoolarYearName, pdf_width / 2, 8, {
        align: "center",
      });

      doc.setFontSize(15);
      doc.setFont("Amiri", "Bold");
      doc.setTextColor("#505050");
      let text =
        t("encaissement.activitiesStateOfUnpaid") +
        " - " +
        (levels[String(level.level)] || level.level);
      doc.text(text, pdf_width / 2, 22, {
        align: "center",
      });

      // Month names translation
      const monthNames = [
        t("months.june"),
        t("months.may"),
        t("months.april"),
        t("months.march"),
        t("months.february"),
        t("months.january"),
        t("months.december"),
        t("months.november"),
        t("months.october"),
        t("months.september"),
      ];

      for (const activity of level.activites) {
        const activityStudents = activity.students || [];
        const anyPositive = (obj) =>
          obj && Object.values(obj).some((v) => Number(v) > 0);
        const hasAnyInscription = (s) =>
          Number(s.inscription) > 0 ||
          Number(s.inscriptionPaid) > 0 ||
          Number(s.inscriptionDiscounted) > 0;

        // Decide payment type PER ACTIVITY, not per-student.
        // Some single-payment activities come with monthsTotal/tranchesTotal objects filled with zeros.
        // We consider an activity "monthly/tranche" only if it has at least one positive total value.
        const activityHasMonthly =
          anyPositive(activity.monthsTotal) || anyPositive(activity.monthsTotalPaid);
        const activityHasTranche =
          anyPositive(activity.tranchesTotal) ||
          anyPositive(activity.tranchesTotalPaid);
        const activityIsSingle = !activityHasMonthly && !activityHasTranche;

        const monthlyStudentsActivity = activityHasMonthly
          ? activityStudents.filter((s) => s.mode === "M")
          : [];
        const trancheStudentsActivity = activityHasTranche
          ? activityStudents.filter((s) => s.mode === "T")
          : [];
        const singlePaymentStudentsActivity = activityIsSingle
          ? activityStudents.filter((s) => hasAnyInscription(s))
          : activityStudents.filter((s) => s.mode === "O");

        const color = "#fcaa97"; // red for unpaid
        const paidColor = "#c8e6c9"; // green for paid
        const discountColor = "#b3d9ff"; // blue for discounted amounts
        if (monthlyStudentsActivity.length > 0) {
          // Title row (same structure as scolarité)
          let array = [
            reverseIfNotArabic([
              {
                content:
                  t("encaissement.monthlyPayment") + " - " + activity.name,
                colSpan: 16,
                styles: {
                  fontStyle: "Bold",
                  fontSize: 10,
                  fillColor: "#e8f4f8",
                },
              },
            ]),
            reverseIfNotArabic([
              {
                content: t("paymentStatus.totalPaye"),
                styles: { fontStyle: "Bold", fontSize: 8 },
              },
              {
                content: t("paymentStatus.totalNonPaye"),
                styles: { fontStyle: "Bold", fontSize: 8 },
              },
              {
                content: monthNames[0],
                styles: { fontStyle: "Bold", fontSize: 9 },
              },
              {
                content: monthNames[1],
                styles: { fontStyle: "Bold", fontSize: 9 },
              },
              {
                content: monthNames[2],
                styles: { fontStyle: "Bold", fontSize: 9 },
              },
              {
                content: monthNames[3],
                styles: { fontStyle: "Bold", fontSize: 9 },
              },
            {
              content: monthNames[4],
              styles: { fontStyle: "Bold", fontSize: 9 },
            },
            {
              content: monthNames[5],
              styles: { fontStyle: "Bold", fontSize: 9 },
            },
            {
              content: monthNames[6],
              styles: { fontStyle: "Bold", fontSize: 9 },
            },
            {
              content: monthNames[7],
              styles: { fontStyle: "Bold", fontSize: 9 },
            },
            {
              content: monthNames[8],
              styles: { fontStyle: "Bold", fontSize: 9 },
            },
            {
              content: monthNames[9],
              styles: { fontStyle: "Bold", fontSize: 9 },
            },
            {
              content: t("scolaritePdf.registration"),
              styles: { fontStyle: "Bold", fontSize: 9 },
            },
            {
              content: levels[String(level.level)] || level.level,
              styles: { fontStyle: "Bold", fontSize: 9 },
            },
            {
              content: this.context.schoolarYearName,
              colSpan: 2,
              styles: { fontStyle: "Bold", fontSize: 9 },
            },
          ]),
        ];

        let arr = [];

        let studentId = 1;
        const monthKeysByCol = [6, 5, 4, 3, 2, 1, 12, 11, 10, 9];

        for (const student of monthlyStudentsActivity) {
          // Calculate inscription paid for this student
          const studentInscriptionPaid =
            Number(student.inscription) > 0
              ? Number(student.inscriptionPaid) || 0
              : Number(student.inscriptionPaid) || 0;

          const studentTotalPaidYear =
            monthKeysByCol.reduce(
              (acc, m) =>
                acc +
                (student.monthsPaid?.[m] || 0) +
                (student.monthsPartialPaid?.[m] || 0),
              0
            ) + studentInscriptionPaid;

          // Total unpaid for the year (months unpaid + inscription unpaid)
          const studentTotalUnpaidYear =
            (student.montantTotalMonths || 0) +
            (Number(student.inscription) || 0);

          const getMonthContent = (monthKey) => {
            const unpaid = this.formatMontant(student.monthsUnpaid?.[monthKey]);
            const paid = this.formatMontant(student.monthsPaid?.[monthKey]);
            return unpaid || paid || "0.000";
          };
          const isMonthDiscounted = (monthKey) =>
            !!student.monthsDiscounted?.[monthKey];
          const allMonthsHaveDiscount = monthKeysByCol.every(
            (m) => student.monthsDiscounted?.[m]
          );
          const isFullYearDiscount =
            student.reductionGlobal || allMonthsHaveDiscount;
          const getMonthFillColor = (monthKey) => {
            if (this.formatMontant(student.monthsUnpaid?.[monthKey]))
              return color;
            if (student.monthsPartialPaid?.[monthKey]) return "#fff9c4";
            if (this.formatMontant(student.monthsPaid?.[monthKey]))
              return !isFullYearDiscount && isMonthDiscounted(monthKey)
                ? discountColor
                : paidColor;
            return "#ffffff";
          };

          const hasPartialInMonth = (monthKey) =>
            !!this.formatMontant(student.monthsPartialPaid?.[monthKey]);
          // Daily activities: month has both unpaid days (red) and fully paid days (green)
          const hasDailyMixedMonth = (monthKey) =>
            student.daily &&
            !!this.formatMontant(student.monthsUnpaid?.[monthKey]) &&
            !!this.formatMontant(student.monthsPaid?.[monthKey]);
          // Need two-row split check
          const needsSplitInMonth = (monthKey) =>
            hasPartialInMonth(monthKey) || hasDailyMixedMonth(monthKey);
          const hasPartialInscription =
            Number(student.inscription) > 0 &&
            Number(student.inscriptionPaid) > 0;
          const hasAnyPartial =
            monthKeysByCol.some(needsSplitInMonth) || hasPartialInscription;

          if (!hasAnyPartial) {
            // Single row
            arr = reverseIfNotArabic([
              {
                content: this.formatMontant(studentTotalPaidYear) || "0.000",
                styles: { fontSize: 8, fillColor: paidColor },
              },
              {
                content: this.formatMontant(studentTotalUnpaidYear) || "0.000",
                styles: { fontSize: 8, fillColor: color },
              },
              {
                content: getMonthContent(6),
                styles: { fontSize: 8, fillColor: getMonthFillColor(6) },
              },
              {
                content: getMonthContent(5),
                styles: { fontSize: 8, fillColor: getMonthFillColor(5) },
              },
              {
                content: getMonthContent(4),
                styles: { fontSize: 8, fillColor: getMonthFillColor(4) },
              },
              {
                content: getMonthContent(3),
                styles: { fontSize: 8, fillColor: getMonthFillColor(3) },
              },
              {
                content: getMonthContent(2),
                styles: { fontSize: 8, fillColor: getMonthFillColor(2) },
              },
              {
                content: getMonthContent(1),
                styles: { fontSize: 8, fillColor: getMonthFillColor(1) },
              },
              {
                content: getMonthContent(12),
                styles: { fontSize: 8, fillColor: getMonthFillColor(12) },
              },
              {
                content: getMonthContent(11),
                styles: { fontSize: 8, fillColor: getMonthFillColor(11) },
              },
              {
                content: getMonthContent(10),
                styles: { fontSize: 8, fillColor: getMonthFillColor(10) },
              },
              {
                content: getMonthContent(9),
                styles: { fontSize: 8, fillColor: getMonthFillColor(9) },
              },
              {
                content: this.getInscriptionContent(student),
                styles: {
                  fontSize: 8,
                  fillColor: this.getInscriptionFillColor(
                    student,
                    paidColor,
                    color,
                    discountColor
                  ),
                },
              },
              {
                content: student.monthly,
                styles: {
                  fillColor: isFullYearDiscount ? discountColor : "#ffffff",
                },
              },
              { content: student.name },
              { content: studentId++ },
            ]);
            array.push(arr);
            continue;
          }

          // Two rows for partial payments
          const row1Cells = [
            {
              content: this.formatMontant(studentTotalPaidYear) || "0.000",
              styles: { fontSize: 8, fillColor: paidColor },
              rowSpan: 2,
            },
            {
              content: this.formatMontant(studentTotalUnpaidYear) || "0.000",
              styles: { fontSize: 8, fillColor: color },
              rowSpan: 2,
            },
          ];
          for (let col = 1; col <= 9; col++) {
            const monthKey = monthKeysByCol[col - 1];
            if (needsSplitInMonth(monthKey)) {
              row1Cells.push({
                content:
                  this.formatMontant(student.monthsUnpaid?.[monthKey]) ||
                  "0.000",
                styles: { fontSize: 8, fillColor: color },
              });
            } else {
              row1Cells.push({
                content: getMonthContent(monthKey),
                styles: {
                  fontSize: 8,
                  fillColor: getMonthFillColor(monthKey),
                },
                rowSpan: 2,
              });
            }
          }
          const monthKeySept = monthKeysByCol[9];
          if (needsSplitInMonth(monthKeySept)) {
            row1Cells.push({
              content:
                this.formatMontant(student.monthsUnpaid?.[monthKeySept]) ||
                "0.000",
              styles: { fontSize: 8, fillColor: color },
            });
          } else {
            row1Cells.push({
              content: getMonthContent(monthKeySept),
              styles: {
                fontSize: 8,
                fillColor: getMonthFillColor(monthKeySept),
              },
              rowSpan: 2,
            });
          }
          // Registration: if partial, show unpaid in row1 and paid in row2; else rowSpan 2
          if (hasPartialInscription) {
            row1Cells.push({
              content: this.formatMontant(student.inscription) || "0.000",
              styles: { fontSize: 8, fillColor: color },
            });
          } else {
            row1Cells.push({
              content: this.getInscriptionContent(student),
              rowSpan: 2,
              styles: {
                fontSize: 8,
                fillColor: this.getInscriptionFillColor(
                  student,
                  paidColor,
                  color,
                  discountColor
                ),
              },
            });
          }
          row1Cells.push(
            {
              content: student.monthly,
              styles: {
                fillColor: isFullYearDiscount ? discountColor : "#ffffff",
              },
              rowSpan: 2,
            },
            { content: student.name, rowSpan: 2 },
            { content: studentId++, rowSpan: 2 }
          );

          const row2Cells = [];
          for (let col = 1; col <= 9; col++) {
            const monthKey = monthKeysByCol[col - 1];
            if (hasPartialInMonth(monthKey)) {
              row2Cells.push({
                content:
                  this.formatMontant(student.monthsPartialPaid?.[monthKey]) ||
                  "0.000",
                styles: { fontSize: 8, fillColor: "#fff9c4" },
              });
            } else if (hasDailyMixedMonth(monthKey)) {
              // Daily activity: paid days shown in green (not yellow)
              row2Cells.push({
                content:
                  this.formatMontant(student.monthsPaid?.[monthKey]) || "0.000",
                styles: { fontSize: 8, fillColor: paidColor },
              });
            }
          }
          if (hasPartialInMonth(monthKeysByCol[9])) {
            row2Cells.push({
              content:
                this.formatMontant(
                  student.monthsPartialPaid?.[monthKeysByCol[9]]
                ) || "0.000",
              styles: { fontSize: 8, fillColor: "#fff9c4" },
            });
          } else if (hasDailyMixedMonth(monthKeysByCol[9])) {
            // Daily activity: paid days shown in green (not yellow)
            row2Cells.push({
              content:
                this.formatMontant(student.monthsPaid?.[monthKeysByCol[9]]) ||
                "0.000",
              styles: { fontSize: 8, fillColor: paidColor },
            });
          }
          // Registration: if partial, add paid amount in row2 (yellow)
          if (hasPartialInscription) {
            row2Cells.push({
              content: this.formatMontant(student.inscriptionPaid) || "0.000",
              styles: { fontSize: 8, fillColor: "#fff9c4" },
            });
          }

          array.push(reverseIfNotArabic(row1Cells));
          array.push(reverseIfNotArabic(row2Cells));
        }

        // Get inscription total for monthly students only (unpaid and paid)
        const monthlyInscriptionTotalActivity = monthlyStudentsActivity.reduce(
          (acc, student) => acc + (student.inscription || 0),
          0
        );
        const fullInscriptionActivity =
          activity.inscription != null && activity.inscription !== false
            ? Number(activity.inscription)
            : 0;
        const monthlyInscriptionPaidTotalActivity =
          monthlyStudentsActivity.reduce((acc, student) => {
            let paid = 0;
            // If inscriptionPaid > 0, use that value directly
            if (Number(student.inscriptionPaid) > 0) {
              paid = Number(student.inscriptionPaid);
            }
            // Else if inscription is 0 (fully paid) and activity has inscription fee, count it as fully paid
            else if (
              Number(student.inscription) === 0 &&
              fullInscriptionActivity > 0
            ) {
              paid = fullInscriptionActivity;
            }
            return acc + paid;
          }, 0);

        // Derive footer totals from the same student rows we display so they always match
        const summedTotalPaidActivity = monthlyStudentsActivity.reduce(
          (acc, s) => {
            const inscriptionPaidForStudent =
              Number(s.inscription) > 0
                ? Number(s.inscriptionPaid) || 0
                : Number(s.inscriptionPaid) || 0;
            const paidMonths = monthKeysByCol.reduce(
              (a, m) =>
                a + (s.monthsPaid?.[m] || 0) + (s.monthsPartialPaid?.[m] || 0),
              0
            );
            return acc + paidMonths + inscriptionPaidForStudent;
          },
          0
        );
        const summedTotalUnpaidActivity = monthlyStudentsActivity.reduce(
          (acc, s) =>
            acc + (s.montantTotalMonths || 0) + (Number(s.inscription) || 0),
          0
        );

        const totalPaidMonths = monthKeysByCol.reduce(
          (acc, m) => acc + (activity.monthsTotalPaid?.[m] || 0),
          0
        );
        array.push(
          reverseIfNotArabic([
            {
              content: this.formatMontant(summedTotalPaidActivity) || "0.000",
              colSpan: 2,
              styles: {
                fontStyle: "Bold",
                fontSize: 10,
                fillColor: paidColor,
              },
            },
            {
              content:
                this.formatMontant(activity.monthsTotalPaid?.[6]) || "0.000",
              styles: {
                fontStyle: "Bold",
                fontSize: 8,
                fillColor: paidColor,
              },
            },
            {
              content:
                this.formatMontant(activity.monthsTotalPaid?.[5]) || "0.000",
              styles: {
                fontStyle: "Bold",
                fontSize: 8,
                fillColor: paidColor,
              },
            },
            {
              content:
                this.formatMontant(activity.monthsTotalPaid?.[4]) || "0.000",
              styles: {
                fontStyle: "Bold",
                fontSize: 8,
                fillColor: paidColor,
              },
            },
            {
              content:
                this.formatMontant(activity.monthsTotalPaid?.[3]) || "0.000",
              styles: {
                fontStyle: "Bold",
                fontSize: 8,
                fillColor: paidColor,
              },
            },
            {
              content:
                this.formatMontant(activity.monthsTotalPaid?.[2]) || "0.000",
              styles: {
                fontStyle: "Bold",
                fontSize: 8,
                fillColor: paidColor,
              },
            },
            {
              content:
                this.formatMontant(activity.monthsTotalPaid?.[1]) || "0.000",
              styles: {
                fontStyle: "Bold",
                fontSize: 8,
                fillColor: paidColor,
              },
            },
            {
              content:
                this.formatMontant(activity.monthsTotalPaid?.[12]) || "0.000",
              styles: {
                fontStyle: "Bold",
                fontSize: 8,
                fillColor: paidColor,
              },
            },
            {
              content:
                this.formatMontant(activity.monthsTotalPaid?.[11]) || "0.000",
              styles: {
                fontStyle: "Bold",
                fontSize: 8,
                fillColor: paidColor,
              },
            },
            {
              content:
                this.formatMontant(activity.monthsTotalPaid?.[10]) || "0.000",
              styles: {
                fontStyle: "Bold",
                fontSize: 8,
                fillColor: paidColor,
              },
            },
            {
              content:
                this.formatMontant(activity.monthsTotalPaid?.[9]) || "0.000",
              styles: {
                fontStyle: "Bold",
                fontSize: 8,
                fillColor: paidColor,
              },
            },
            {
              content:
                this.formatMontant(monthlyInscriptionPaidTotalActivity) ||
                "0.000",
              styles: {
                fontStyle: "Bold",
                fontSize: 10,
                fillColor: paidColor,
              },
            },
            {
              content: t("paymentStatus.totalPaye"),
              colSpan: 3,
              styles: { fontSize: 11, lineWidth: 0.1 },
            },
          ]),
          reverseIfNotArabic([
            {
              content: this.formatMontant(summedTotalUnpaidActivity) || "0.000",
              colSpan: 2,
              styles: {
                fontSize: 10,
                fontStyle: "Bold",
                fillColor: color,
              },
            },
            {
              content: this.formatMontant(activity.monthsTotal[6]) || "0.000",
              styles: { fontStyle: "Bold", fontSize: 8, fillColor: color },
            },
            {
              content: this.formatMontant(activity.monthsTotal[5]) || "0.000",
              styles: { fontStyle: "Bold", fontSize: 8, fillColor: color },
            },
            {
              content: this.formatMontant(activity.monthsTotal[4]) || "0.000",
              styles: { fontStyle: "Bold", fontSize: 8, fillColor: color },
            },
            {
              content: this.formatMontant(activity.monthsTotal[3]) || "0.000",
              styles: { fontStyle: "Bold", fontSize: 8, fillColor: color },
            },
            {
              content: this.formatMontant(activity.monthsTotal[2]) || "0.000",
              styles: { fontStyle: "Bold", fontSize: 8, fillColor: color },
            },
            {
              content: this.formatMontant(activity.monthsTotal[1]) || "0.000",
              styles: { fontStyle: "Bold", fontSize: 8, fillColor: color },
            },
            {
              content: this.formatMontant(activity.monthsTotal[12]) || "0.000",
              styles: { fontStyle: "Bold", fontSize: 8, fillColor: color },
            },
            {
              content: this.formatMontant(activity.monthsTotal[11]) || "0.000",
              styles: { fontStyle: "Bold", fontSize: 8, fillColor: color },
            },
            {
              content: this.formatMontant(activity.monthsTotal[10]) || "0.000",
              styles: { fontStyle: "Bold", fontSize: 8, fillColor: color },
            },
            {
              content: this.formatMontant(activity.monthsTotal[9]) || "0.000",
              styles: { fontStyle: "Bold", fontSize: 8, fillColor: color },
            },
            {
              content:
                this.formatMontant(monthlyInscriptionTotalActivity) || "0.000",
              styles: { fontStyle: "Bold", fontSize: 8, fillColor: color },
            },
            {
              content: t("paymentStatus.totalNonPaye"),
              colSpan: 3,
              styles: { fontSize: 11, lineWidth: 0.1 },
            },
          ])
        );

        // fix table
        for (let row = 0; row < array.length; row++) {
          array[row] = array[row].filter((cell) => cell !== cellToDelete);
        }

        // Column widths: Registration before September (col 11); fixed cols (0,1,11,13,14,15) = 18+18+19+9+40+16; 10 month cols share the rest
        const activityMonthlyFixedWidth = 18 + 18 + 19 + 9 + 40 + 16;
        const activityMonthW =
          (fullTableWidthActivity - activityMonthlyFixedWidth) / 10;
        doc.autoTable({
          theme: "grid",
          tableWidth: fullTableWidthActivity,
          styles: {
            font: "Amiri",
            halign: "center",
            fontSize: 9,
            valign: "middle",
            fontStyle: "normal",
            lineColor: "#363636",
            lineWidth: 0.1,
            cellPadding: {
              right: 0,
              left: 0,
              top: 1,
              bottom: 1,
            },
          },
          pageBreak: "avoid",
          body: array,
          columnStyles: reverseColumnStyles(
            {
              0: { cellWidth: 18 },
              1: { cellWidth: 18 },
              2: { cellWidth: activityMonthW },
              3: { cellWidth: activityMonthW },
              4: { cellWidth: activityMonthW },
              5: { cellWidth: activityMonthW },
              6: { cellWidth: activityMonthW },
              7: { cellWidth: activityMonthW },
              8: { cellWidth: activityMonthW },
              9: { cellWidth: activityMonthW },
              10: { cellWidth: activityMonthW },
              11: { cellWidth: 19 },
              12: { cellWidth: activityMonthW },
              13: { cellWidth: 9 },
              14: { cellWidth: 40 },
              15: { cellWidth: 16 },
            },
            16
          ),
          margin: {
            top: 35,
            left: tableMarginHorizontalActivity,
            right: tableMarginHorizontalActivity,
            bottom: LEGEND_FOOTER_HEIGHT_MM,
          },
          didDrawPage: function (data) {
            // Reseting top margin. The change will be reflected only after print the first page.
            data.settings.margin.top = 10;
          },
        });
        }

        // ==============================================
        // TABLE: Paiement par tranche (per activity) – dynamic tranches
        // ==============================================
        if (trancheStudentsActivity.length > 0) {
          // Determine actual tranches for this activity (from activity.tranches)
          const activityTranches = (activity.tranches || [])
            .map((t) => t.trancheNumber)
            .sort((a, b) => b - a); // Descending: [4, 3, 2, 1] or [3, 2, 1], etc.

          const numActivityTranches = activityTranches.length;
          const numTrancheCols = 2 + numActivityTranches + 1 + 2; // Total paid, Total unpaid, Tranches, Registration, Name, ID

          // Build header row with actual tranches
          const trancheHeaderRow = [
            {
              content: t("paymentStatus.totalPaye"),
              styles: { fontStyle: "Bold", fontSize: 8 },
            },
            {
              content: t("paymentStatus.totalNonPaye"),
              styles: { fontStyle: "Bold", fontSize: 8 },
            },
          ];
          activityTranches.forEach((num) => {
            trancheHeaderRow.push({
              content: t("scolaritePdf.installment", { number: num }),
              styles: { fontStyle: "Bold", fontSize: 9 },
            });
          });
          trancheHeaderRow.push(
            {
              content: t("scolaritePdf.registration"),
              styles: { fontStyle: "Bold", fontSize: 9 },
            },
            {
              content: this.context.schoolarYearName,
              colSpan: 2,
              styles: { fontStyle: "Bold", fontSize: 9 },
            }
          );

          const trancheArray = [
            reverseIfNotArabic([
              {
                content:
                  t("encaissement.installmentPayment") + " - " + activity.name,
                colSpan: numTrancheCols,
                styles: {
                  fontStyle: "Bold",
                  fontSize: 10,
                  fillColor: "#e8f4f8",
                },
              },
            ]),
            reverseIfNotArabic(trancheHeaderRow),
          ];
          let trancheStudentId = 1;
          const trancheKeysByCol = activityTranches;
          for (const student of trancheStudentsActivity) {
            // Calculate inscription paid for this student
            const studentInscriptionPaid =
              Number(student.inscription) > 0
                ? Number(student.inscriptionPaid) || 0
                : Number(student.inscriptionPaid) || 0;

            const studentTotalPaidYear =
              trancheKeysByCol.reduce(
                (acc, n) =>
                  acc +
                  (student.tranchesPaid?.[n] || 0) +
                  (student.tranchesPartialPaid?.[n] || 0),
                0
              ) + studentInscriptionPaid;

            // Total unpaid for the year (tranches unpaid + inscription unpaid)
            const studentTotalUnpaidYear =
              (student.montantTotalTranches || 0) +
              (Number(student.inscription) || 0);

            const getTrancheContent = (trancheKey) => {
              const unpaid = this.formatMontant(
                student.tranchesUnpaid?.[trancheKey]
              );
              const paid = this.formatMontant(
                student.tranchesPaid?.[trancheKey]
              );
              return unpaid || paid || "0.000";
            };
            const isTrancheDiscounted = (trancheKey) =>
              !!student.tranchesDiscounted?.[trancheKey];
            const getTrancheFillColor = (trancheKey) => {
              if (this.formatMontant(student.tranchesUnpaid?.[trancheKey]))
                return color;
              if (student.tranchesPartialPaid?.[trancheKey]) return "#fff9c4";
              if (this.formatMontant(student.tranchesPaid?.[trancheKey]))
                return !student.reductionGlobal &&
                  isTrancheDiscounted(trancheKey)
                  ? discountColor
                  : paidColor;
              return "#ffffff";
            };

            const hasPartialInTranche = (trancheKey) =>
              !!this.formatMontant(student.tranchesPartialPaid?.[trancheKey]);
            const hasPartialInscription =
              Number(student.inscription) > 0 &&
              Number(student.inscriptionPaid) > 0;
            const hasAnyPartial =
              trancheKeysByCol.some(hasPartialInTranche) ||
              hasPartialInscription;

            if (!hasAnyPartial) {
              // Single row: build cells for actual tranches only
              const studentRowCells = [
                {
                  content: this.formatMontant(studentTotalPaidYear) || "0.000",
                  styles: { fontSize: 8, fillColor: paidColor },
                },
                {
                  content:
                    this.formatMontant(studentTotalUnpaidYear) || "0.000",
                  styles: { fontSize: 8, fillColor: color },
                },
              ];
              trancheKeysByCol.forEach((trancheKey) => {
                studentRowCells.push({
                  content: getTrancheContent(trancheKey),
                  styles: {
                    fontSize: 8,
                    fillColor: getTrancheFillColor(trancheKey),
                  },
                });
              });
              studentRowCells.push(
                {
                  content: this.getInscriptionContent(student),
                  styles: {
                    fontSize: 8,
                    fillColor: this.getInscriptionFillColor(
                      student,
                      paidColor,
                      color,
                      discountColor
                    ),
                  },
                },
                { content: student.name },
                { content: trancheStudentId++ }
              );
              trancheArray.push(reverseIfNotArabic(studentRowCells));
              continue;
            }

            // Two rows for partial payments
            const row1Cells = [
              {
                content: this.formatMontant(studentTotalPaidYear) || "0.000",
                styles: { fontSize: 8, fillColor: paidColor },
                rowSpan: 2,
              },
              {
                content: this.formatMontant(studentTotalUnpaidYear) || "0.000",
                styles: { fontSize: 8, fillColor: color },
                rowSpan: 2,
              },
            ];
            trancheKeysByCol.forEach((trancheKey) => {
              if (hasPartialInTranche(trancheKey)) {
                row1Cells.push({
                  content:
                    this.formatMontant(student.tranchesUnpaid?.[trancheKey]) ||
                    "0.000",
                  styles: { fontSize: 8, fillColor: color },
                });
              } else {
                row1Cells.push({
                  content: getTrancheContent(trancheKey),
                  styles: {
                    fontSize: 8,
                    fillColor: getTrancheFillColor(trancheKey),
                  },
                  rowSpan: 2,
                });
              }
            });
            // Registration: if partial, show unpaid in row1 and paid in row2; else rowSpan 2
            if (hasPartialInscription) {
              row1Cells.push({
                content: this.formatMontant(student.inscription) || "0.000",
                styles: { fontSize: 8, fillColor: color },
              });
            } else {
              row1Cells.push({
                content: this.getInscriptionContent(student),
                rowSpan: 2,
                styles: {
                  fontSize: 8,
                  fillColor: this.getInscriptionFillColor(
                    student,
                    paidColor,
                    color,
                    discountColor
                  ),
                },
              });
            }
            row1Cells.push(
              { content: student.name, rowSpan: 2 },
              { content: trancheStudentId++, rowSpan: 2 }
            );

            const row2Cells = [];
            trancheKeysByCol.forEach((trancheKey) => {
              if (hasPartialInTranche(trancheKey)) {
                row2Cells.push({
                  content:
                    this.formatMontant(
                      student.tranchesPartialPaid?.[trancheKey]
                    ) || "0.000",
                  styles: { fontSize: 8, fillColor: "#fff9c4" },
                });
              }
            });
            // Registration: if partial, add paid amount in row2 (yellow)
            if (hasPartialInscription) {
              row2Cells.push({
                content: this.formatMontant(student.inscriptionPaid) || "0.000",
                styles: { fontSize: 8, fillColor: "#fff9c4" },
              });
            }

            trancheArray.push(reverseIfNotArabic(row1Cells));
            trancheArray.push(reverseIfNotArabic(row2Cells));
          }
          const trancheInscriptionTotal = trancheStudentsActivity.reduce(
            (acc, student) => acc + (student.inscription || 0),
            0
          );
          const fullInscriptionActivityTranche =
            activity.inscription != null && activity.inscription !== false
              ? Number(activity.inscription)
              : 0;
          const trancheInscriptionPaidTotal = trancheStudentsActivity.reduce(
            (acc, student) => {
              let paid = 0;
              // If inscriptionPaid > 0, use that value directly
              if (Number(student.inscriptionPaid) > 0) {
                paid = Number(student.inscriptionPaid);
              }
              // Else if inscription is 0 (fully paid) and activity has inscription fee, count it as fully paid
              else if (
                Number(student.inscription) === 0 &&
                fullInscriptionActivityTranche > 0
              ) {
                paid = fullInscriptionActivityTranche;
              }
              return acc + paid;
            },
            0
          );
          // Derive footer totals from the same student rows we display so they always match
          const summedTotalPaidTranchesActivity =
            trancheStudentsActivity.reduce((acc, s) => {
              const inscriptionPaidForStudent =
                Number(s.inscription) > 0
                  ? Number(s.inscriptionPaid) || 0
                  : Number(s.inscriptionPaid) || 0;
              const paidTranches = activityTranches.reduce(
                (a, n) =>
                  a +
                  (s.tranchesPaid?.[n] || 0) +
                  (s.tranchesPartialPaid?.[n] || 0),
                0
              );
              return acc + paidTranches + inscriptionPaidForStudent;
            }, 0);
          const summedTotalUnpaidTranchesActivity =
            trancheStudentsActivity.reduce(
              (acc, s) =>
                acc +
                (s.montantTotalTranches || 0) +
                (Number(s.inscription) || 0),
              0
            );
          const trancheCellToDelete = {};
          const totalPaidTranches =
            (activity.tranchesTotalPaid &&
              activityTranches.reduce(
                (acc, n) => acc + (activity.tranchesTotalPaid[n] || 0),
                0
              )) ||
            0;

          // Build footer paid row with actual tranches
          const activityFooterPaidRow = [
            {
              content:
                this.formatMontant(summedTotalPaidTranchesActivity) || "0.000",
              colSpan: 2,
              styles: {
                fontSize: 10,
                fontStyle: "Bold",
                fillColor: paidColor,
              },
            },
          ];
          activityTranches.forEach((num) => {
            activityFooterPaidRow.push({
              content:
                this.formatMontant(activity.tranchesTotalPaid?.[num]) ||
                "0.000",
              styles: {
                fontStyle: "Bold",
                fontSize: 8,
                fillColor: paidColor,
              },
            });
          });
          activityFooterPaidRow.push(
            {
              content:
                this.formatMontant(trancheInscriptionPaidTotal) || "0.000",
              styles: {
                fontStyle: "Bold",
                fontSize: 10,
                fillColor: paidColor,
              },
            },
            {
              content: t("paymentStatus.totalPaye"),
              colSpan: 2,
              styles: { fontSize: 11, lineWidth: 0.1 },
            }
          );

          // Build footer unpaid row with actual tranches
          const activityFooterUnpaidRow = [
            {
              content:
                this.formatMontant(summedTotalUnpaidTranchesActivity) ||
                "0.000",
              colSpan: 2,
              styles: {
                fontSize: 10,
                fontStyle: "Bold",
                fillColor: color,
              },
            },
          ];
          activityTranches.forEach((num) => {
            activityFooterUnpaidRow.push({
              content:
                this.formatMontant(activity.tranchesTotal?.[num]) || "0.000",
              styles: { fontStyle: "Bold", fontSize: 8, fillColor: color },
            });
          });
          activityFooterUnpaidRow.push(
            {
              content: this.formatMontant(trancheInscriptionTotal) || "0.000",
              styles: {
                fontStyle: "Bold",
                fontSize: 10,
                fillColor: color,
              },
            },
            {
              content: t("paymentStatus.totalNonPaye"),
              colSpan: 2,
              styles: { fontSize: 11, lineWidth: 0.1 },
            }
          );

          trancheArray.push(reverseIfNotArabic(activityFooterPaidRow));
          trancheArray.push(reverseIfNotArabic(activityFooterUnpaidRow));
          for (let row = 0; row < trancheArray.length; row++) {
            trancheArray[row] = trancheArray[row].filter(
              (cell) => cell !== trancheCellToDelete
            );
          }
          const activityTrancheFixedWidth = 18 + 18 + 40 + 9 + 19;
          const activityTrancheW =
            (fullTableWidthActivity - activityTrancheFixedWidth) /
            numActivityTranches;

          // Build columnStyles dynamically based on number of tranches
          const trancheColumnStyles = {
            0: { cellWidth: 18 }, // Total paid
            1: { cellWidth: 18 }, // Total unpaid
          };
          for (let i = 0; i < numActivityTranches; i++) {
            trancheColumnStyles[2 + i] = { cellWidth: activityTrancheW };
          }
          trancheColumnStyles[2 + numActivityTranches] = { cellWidth: 19 }; // Inscription
          trancheColumnStyles[3 + numActivityTranches] = { cellWidth: 40 }; // Name
          trancheColumnStyles[4 + numActivityTranches] = { cellWidth: 9 }; // ID

          doc.autoTable({
            theme: "grid",
            styles: {
              font: "Amiri",
              halign: "center",
              fontSize: 9,
              valign: "middle",
              fontStyle: "normal",
              lineColor: "#363636",
              lineWidth: 0.1,
              cellPadding: { right: 0, left: 0, top: 1, bottom: 1 },
            },
            pageBreak: "avoid",
            tableWidth: fullTableWidthActivity,
            body: trancheArray,
            columnStyles: reverseColumnStyles(
              trancheColumnStyles,
              numTrancheCols
            ),
            margin: {
              top: 35,
              left: tableMarginHorizontalActivity,
              right: tableMarginHorizontalActivity,
              bottom: LEGEND_FOOTER_HEIGHT_MM,
            },
            didDrawPage: function (data) {
              data.settings.margin.top = 10;
            },
          });
        }

        // ==============================================
        // TABLE: Paiement unique (per activity)
        // ==============================================
        if (singlePaymentStudentsActivity.length > 0) {
          const singleLabel = t("encaissement.singlePayment");
          const singleTitle = `${singleLabel} - ${activity.name}`;

          // Single-payment table: keep it simple (only registration + student + #).
          // Totals (paid/unpaid) are shown only in footer.
          const numSingleCols = 3;
          const singleRows = [
            reverseIfNotArabic([
              {
                content: singleTitle,
                colSpan: numSingleCols,
                styles: {
                  fontStyle: "Bold",
                  fontSize: 10,
                  fillColor: "#e8f4f8",
                },
              },
            ]),
            reverseIfNotArabic([
              {
                content: t("scolaritePdf.registration"),
                styles: { fontStyle: "Bold", fontSize: 9 },
              },
              {
                content: this.context.schoolarYearName,
                colSpan: 2,
                styles: { fontStyle: "Bold", fontSize: 9 },
              },
            ]),
          ];

          let studentId = 1;
          let singleTotalPaid = 0;
          let singleTotalUnpaid = 0;
          for (const s of singlePaymentStudentsActivity) {
            const paid = Number(s.inscriptionPaid) || 0;
            const unpaid = Number(s.inscription) || 0;
            singleTotalPaid += paid;
            singleTotalUnpaid += unpaid;

            // Same "inscription" logic as monthly/tranche tables:
            // - green if fully paid, red if unpaid, yellow if partial, blue if paid with discount
            const hasPartialInscription =
              Number(s.inscription) > 0 && Number(s.inscriptionPaid) > 0;

            if (!hasPartialInscription) {
              singleRows.push(
                reverseIfNotArabic([
                  {
                    content: this.getInscriptionContent(s),
                    styles: {
                      fontSize: 8,
                      fillColor: this.getInscriptionFillColor(
                        s,
                        paidColor,
                        color,
                        discountColor
                      ),
                    },
                  },
                  { content: s.name },
                  { content: studentId++ },
                ])
              );
              continue;
            }

            // Partial inscription: show unpaid (red) + paid (yellow) on two rows
            const nameCell = { content: s.name, rowSpan: 2 };
            const idCell = { content: studentId++, rowSpan: 2 };

            singleRows.push(
              reverseIfNotArabic([
                {
                  content: this.formatMontant(s.inscription) || "0.000",
                  styles: { fontSize: 8, fillColor: color },
                },
                nameCell,
                idCell,
              ]),
              reverseIfNotArabic([
                {
                  content: this.formatMontant(s.inscriptionPaid) || "0.000",
                  styles: { fontSize: 8, fillColor: "#fff9c4" },
                },
              ])
            );
          }

          // Footer rows: total paid (green) then total unpaid (red)
          singleRows.push(
            reverseIfNotArabic([
              {
                content: this.formatMontant(singleTotalPaid) || "0.000",
                colSpan: 1,
                styles: {
                  fontStyle: "Bold",
                  fontSize: 10,
                  fillColor: paidColor,
                },
              },
              {
                content: t("paymentStatus.totalPaye"),
                colSpan: 2,
                styles: { fontSize: 11, lineWidth: 0.1 },
              },
            ]),
            reverseIfNotArabic([
              {
                content: this.formatMontant(singleTotalUnpaid) || "0.000",
                colSpan: 1,
                styles: { fontStyle: "Bold", fontSize: 10, fillColor: color },
              },
              {
                content: t("paymentStatus.totalNonPaye"),
                colSpan: 2,
                styles: { fontSize: 11, lineWidth: 0.1 },
              },
            ])
          );

          const singleRegistrationW = 70;
          const singleIdW = 18;
          const singleFixedWidth = singleRegistrationW + singleIdW;
          const singleNameW = fullTableWidthActivity - singleFixedWidth;
          doc.autoTable({
            theme: "grid",
            tableWidth: fullTableWidthActivity,
            styles: {
              font: "Amiri",
              halign: "center",
              fontSize: 9,
              valign: "middle",
              fontStyle: "normal",
              lineColor: "#363636",
              lineWidth: 0.1,
              cellPadding: { right: 1, left: 1, top: 1, bottom: 1 },
            },
            pageBreak: "avoid",
            body: singleRows,
            columnStyles: reverseColumnStyles(
              {
                0: { cellWidth: singleRegistrationW }, // registration
                1: { cellWidth: singleNameW }, // student
                2: { cellWidth: singleIdW }, // #
              },
              numSingleCols
            ),
            margin: {
              top: 35,
              left: tableMarginHorizontalActivity,
              right: tableMarginHorizontalActivity,
              bottom: LEGEND_FOOTER_HEIGHT_MM,
            },
            didDrawPage: function (data) {
              data.settings.margin.top = 10;
            },
          });
        }
      }

      doc.addPage();
    }

    // Month names for summary table
    const summaryMonthNames = [
      t("months.june"),
      t("months.may"),
      t("months.april"),
      t("months.march"),
      t("months.february"),
      t("months.january"),
      t("months.december"),
      t("months.november"),
      t("months.october"),
      t("months.september"),
    ];

    const paidColorSummary = "#c8e6c9";
    const colorSummary = "#fcaa97";

    // ==============================================
    // SUMMARY: PAIEMENT MENSUEL (ALL LEVELS) – same logic as scolarité
    // ==============================================
    const monthlySummary = [
      // Title row
      reverseIfNotArabic([
        {
          content: t("encaissement.monthlyPayment"),
          colSpan: 13,
          styles: { fontStyle: "Bold", fontSize: 10, fillColor: "#e8f4f8" },
        },
      ]),
      // Header row (internal order: ... Oct, Sept, Registration, Levels → LTR shows Registration before Sept)
      reverseIfNotArabic([
        {
          content: t("scolaritePdf.activities"),
          styles: { fontStyle: "Bold", fontSize: 10 },
        },
        {
          content: summaryMonthNames[0],
          styles: { fontStyle: "Bold", fontSize: 10 },
        },
        {
          content: summaryMonthNames[1],
          styles: { fontStyle: "Bold", fontSize: 10 },
        },
        {
          content: summaryMonthNames[2],
          styles: { fontStyle: "Bold", fontSize: 10 },
        },
        {
          content: summaryMonthNames[3],
          styles: { fontStyle: "Bold", fontSize: 10 },
        },
        {
          content: summaryMonthNames[4],
          styles: { fontStyle: "Bold", fontSize: 10 },
        },
        {
          content: summaryMonthNames[5],
          styles: { fontStyle: "Bold", fontSize: 10 },
        },
        {
          content: summaryMonthNames[6],
          styles: { fontStyle: "Bold", fontSize: 10 },
        },
        {
          content: summaryMonthNames[7],
          styles: { fontStyle: "Bold", fontSize: 10 },
        },
        {
          content: summaryMonthNames[8],
          styles: { fontStyle: "Bold", fontSize: 10 },
        },
        {
          content: summaryMonthNames[9],
          styles: { fontStyle: "Bold", fontSize: 10 },
        },
        {
          content: t("scolaritePdf.registration"),
          styles: { fontStyle: "Bold", fontSize: 10 },
        },
        {
          content: t("scolaritePdf.levels"),
          styles: { fontStyle: "Bold", fontSize: 10 },
        },
      ]),
    ];

    try {
      const logoX = isArabic ? 10 : pdf_width - 30; // Left for Arabic, right for others
      doc.addImage(imgData, "JPEG", logoX, 8, 20, 20);
    } catch (error) {
      console.log(error);
    }

    doc.setFont("Amiri", "normal");
    doc.setFontSize(10);

    const options = {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    };

    doc.text(
      new Date().toLocaleDateString(locale, options),
      isArabic ? pdf_width - 17 : 17,
      8,
      {
        align: isArabic ? "right" : "left",
      }
    );

    doc.setFontSize(15);
    doc.setFont("Amiri", "Bold");
    doc.setTextColor("#505050");
    let text =
      t("encaissement.activitiesStateOfUnpaid") +
      " - " +
      t("scolaritePdf.allLevels");
    doc.text(text, pdf_width / 2, 22, {
      align: "center",
    });

    for (const level of impayeData.levels) {
      const levelTotalPaid = level.monthsTotalPaid
        ? [6, 5, 4, 3, 2, 1, 12, 11, 10, 9].reduce(
            (acc, m) => acc + (level.monthsTotalPaid[m] || 0),
            0
          )
        : 0;
      const levelTotalPaidWithInscription =
        levelTotalPaid + (Number(level.montantTotalInscriptionPaid) || 0);
      const levelTotalUnpaidWithInscription =
        (Number(level.montantTotalMonths) || 0) +
        (Number(level.montantTotalInscription) || 0);

      monthlySummary.push(
        reverseIfNotArabic([
          {
            content: this.formatMontant(levelTotalPaidWithInscription) || "0.000",
            styles: {
              fontSize: 8,
              fillColor: paidColorSummary,
              fontStyle: "Bold",
            },
          },
          {
            content: this.formatMontant(level.monthsTotalPaid?.[6]) || "0.000",
            styles: { fontSize: 8, fillColor: paidColorSummary },
          },
          {
            content: this.formatMontant(level.monthsTotalPaid?.[5]) || "0.000",
            styles: { fontSize: 8, fillColor: paidColorSummary },
          },
          {
            content: this.formatMontant(level.monthsTotalPaid?.[4]) || "0.000",
            styles: { fontSize: 8, fillColor: paidColorSummary },
          },
          {
            content: this.formatMontant(level.monthsTotalPaid?.[3]) || "0.000",
            styles: { fontSize: 8, fillColor: paidColorSummary },
          },
          {
            content: this.formatMontant(level.monthsTotalPaid?.[2]) || "0.000",
            styles: { fontSize: 8, fillColor: paidColorSummary },
          },
          {
            content: this.formatMontant(level.monthsTotalPaid?.[1]) || "0.000",
            styles: { fontSize: 8, fillColor: paidColorSummary },
          },
          {
            content: this.formatMontant(level.monthsTotalPaid?.[12]) || "0.000",
            styles: { fontSize: 8, fillColor: paidColorSummary },
          },
          {
            content: this.formatMontant(level.monthsTotalPaid?.[11]) || "0.000",
            styles: { fontSize: 8, fillColor: paidColorSummary },
          },
          {
            content: this.formatMontant(level.monthsTotalPaid?.[10]) || "0.000",
            styles: { fontSize: 8, fillColor: paidColorSummary },
          },
          {
            content: this.formatMontant(level.monthsTotalPaid?.[9]) || "0.000",
            styles: { fontSize: 8, fillColor: paidColorSummary },
          },
          {
            content:
              this.formatMontant(level.montantTotalInscriptionPaid) || "0.000",
            styles: { fontSize: 8, fillColor: paidColorSummary },
          },
          {
            content: levels[String(level.level)] || level.level,
            styles: { fontSize: 9, fontStyle: "Bold" },
            rowSpan: 2,
          },
        ])
      );

      monthlySummary.push(
        reverseIfNotArabic([
          {
            content:
              this.formatMontant(levelTotalUnpaidWithInscription) || "0.000",
            styles: { fontSize: 8, fillColor: colorSummary, fontStyle: "Bold" },
          },
          {
            content: this.formatMontant(level.monthsTotal[6]) || "0.000",
            styles: { fontSize: 8, fillColor: colorSummary },
          },
          {
            content: this.formatMontant(level.monthsTotal[5]) || "0.000",
            styles: { fontSize: 8, fillColor: colorSummary },
          },
          {
            content: this.formatMontant(level.monthsTotal[4]) || "0.000",
            styles: { fontSize: 8, fillColor: colorSummary },
          },
          {
            content: this.formatMontant(level.monthsTotal[3]) || "0.000",
            styles: { fontSize: 8, fillColor: colorSummary },
          },
          {
            content: this.formatMontant(level.monthsTotal[2]) || "0.000",
            styles: { fontSize: 8, fillColor: colorSummary },
          },
          {
            content: this.formatMontant(level.monthsTotal[1]) || "0.000",
            styles: { fontSize: 8, fillColor: colorSummary },
          },
          {
            content: this.formatMontant(level.monthsTotal[12]) || "0.000",
            styles: { fontSize: 8, fillColor: colorSummary },
          },
          {
            content: this.formatMontant(level.monthsTotal[11]) || "0.000",
            styles: { fontSize: 8, fillColor: colorSummary },
          },
          {
            content: this.formatMontant(level.monthsTotal[10]) || "0.000",
            styles: { fontSize: 8, fillColor: colorSummary },
          },
          {
            content: this.formatMontant(level.monthsTotal[9]) || "0.000",
            styles: { fontSize: 8, fillColor: colorSummary },
          },
          {
            content:
              this.formatMontant(level.montantTotalInscription) || "0.000",
            styles: { fontSize: 8, fillColor: colorSummary },
          },
        ])
      );
    }

    const overallTotalPaidActivity = impayeData.monthsTotalPaid
      ? [6, 5, 4, 3, 2, 1, 12, 11, 10, 9].reduce(
          (acc, m) => acc + (impayeData.monthsTotalPaid[m] || 0),
          0
        )
      : 0;
    const overallTotalPaidActivityWithInscription =
      overallTotalPaidActivity + (Number(impayeData.montantTotalInscriptionPaid) || 0);
    const overallTotalUnpaidActivityWithInscription =
      (Number(impayeData.montantTotalMonths) || 0) +
      (Number(impayeData.montantTotalInscription) || 0);

    monthlySummary.push(
      reverseIfNotArabic([
        {
          content:
            this.formatMontant(overallTotalPaidActivityWithInscription) ||
            "0.000",
          styles: {
            fontStyle: "Bold",
            fontSize: 10,
            fillColor: paidColorSummary,
          },
        },
        {
          content:
            this.formatMontant(impayeData.monthsTotalPaid?.[6]) || "0.000",
          styles: {
            fontStyle: "Bold",
            fontSize: 8,
            fillColor: paidColorSummary,
          },
        },
        {
          content:
            this.formatMontant(impayeData.monthsTotalPaid?.[5]) || "0.000",
          styles: {
            fontStyle: "Bold",
            fontSize: 8,
            fillColor: paidColorSummary,
          },
        },
        {
          content:
            this.formatMontant(impayeData.monthsTotalPaid?.[4]) || "0.000",
          styles: {
            fontStyle: "Bold",
            fontSize: 8,
            fillColor: paidColorSummary,
          },
        },
        {
          content:
            this.formatMontant(impayeData.monthsTotalPaid?.[3]) || "0.000",
          styles: {
            fontStyle: "Bold",
            fontSize: 8,
            fillColor: paidColorSummary,
          },
        },
        {
          content:
            this.formatMontant(impayeData.monthsTotalPaid?.[2]) || "0.000",
          styles: {
            fontStyle: "Bold",
            fontSize: 8,
            fillColor: paidColorSummary,
          },
        },
        {
          content:
            this.formatMontant(impayeData.monthsTotalPaid?.[1]) || "0.000",
          styles: {
            fontStyle: "Bold",
            fontSize: 8,
            fillColor: paidColorSummary,
          },
        },
        {
          content:
            this.formatMontant(impayeData.monthsTotalPaid?.[12]) || "0.000",
          styles: {
            fontStyle: "Bold",
            fontSize: 8,
            fillColor: paidColorSummary,
          },
        },
        {
          content:
            this.formatMontant(impayeData.monthsTotalPaid?.[11]) || "0.000",
          styles: {
            fontStyle: "Bold",
            fontSize: 8,
            fillColor: paidColorSummary,
          },
        },
        {
          content:
            this.formatMontant(impayeData.monthsTotalPaid?.[10]) || "0.000",
          styles: {
            fontStyle: "Bold",
            fontSize: 8,
            fillColor: paidColorSummary,
          },
        },
        {
          content:
            this.formatMontant(impayeData.monthsTotalPaid?.[9]) || "0.000",
          styles: {
            fontStyle: "Bold",
            fontSize: 8,
            fillColor: paidColorSummary,
          },
        },
        {
          content:
            this.formatMontant(impayeData.montantTotalInscriptionPaid) ||
            "0.000",
          styles: {
            fontStyle: "Bold",
            fontSize: 10,
            fillColor: paidColorSummary,
          },
        },
        {
          content: t("paymentStatus.totalPaye"),
          styles: { fontSize: 11, lineWidth: 0.1 },
        },
      ]),
      reverseIfNotArabic([
        {
          content:
            this.formatMontant(overallTotalUnpaidActivityWithInscription) ||
            "0.000",
          styles: { fontStyle: "Bold", fontSize: 10, fillColor: colorSummary },
        },
        {
          content: this.formatMontant(impayeData.monthsTotal[6]) || "0.000",
          styles: { fontStyle: "Bold", fontSize: 8, fillColor: colorSummary },
        },
        {
          content: this.formatMontant(impayeData.monthsTotal[5]) || "0.000",
          styles: { fontStyle: "Bold", fontSize: 8, fillColor: colorSummary },
        },
        {
          content: this.formatMontant(impayeData.monthsTotal[4]) || "0.000",
          styles: { fontStyle: "Bold", fontSize: 8, fillColor: colorSummary },
        },
        {
          content: this.formatMontant(impayeData.monthsTotal[3]) || "0.000",
          styles: { fontStyle: "Bold", fontSize: 8, fillColor: colorSummary },
        },
        {
          content: this.formatMontant(impayeData.monthsTotal[2]) || "0.000",
          styles: { fontStyle: "Bold", fontSize: 8, fillColor: colorSummary },
        },
        {
          content: this.formatMontant(impayeData.monthsTotal[1]) || "0.000",
          styles: { fontStyle: "Bold", fontSize: 8, fillColor: colorSummary },
        },
        {
          content: this.formatMontant(impayeData.monthsTotal[12]) || "0.000",
          styles: { fontStyle: "Bold", fontSize: 8, fillColor: colorSummary },
        },
        {
          content: this.formatMontant(impayeData.monthsTotal[11]) || "0.000",
          styles: { fontStyle: "Bold", fontSize: 8, fillColor: colorSummary },
        },
        {
          content: this.formatMontant(impayeData.monthsTotal[10]) || "0.000",
          styles: { fontStyle: "Bold", fontSize: 8, fillColor: colorSummary },
        },
        {
          content: this.formatMontant(impayeData.monthsTotal[9]) || "0.000",
          styles: { fontStyle: "Bold", fontSize: 8, fillColor: colorSummary },
        },
        {
          content:
            this.formatMontant(impayeData.montantTotalInscription) || "0.000",
          styles: { fontStyle: "Bold", fontSize: 8, fillColor: colorSummary },
        },
        {
          content: t("paymentStatus.totalNonPaye"),
          styles: { fontSize: 11, lineWidth: 0.1 },
        },
      ])
    );

    // fix table
    for (let row = 0; row < monthlySummary.length; row++) {
      monthlySummary[row] = monthlySummary[row].filter(
        (cell) => cell !== cellToDelete
      );
    }

    // Summary monthly: first col 25, Registration before September (col 10), Level 22; 10 month cols share the rest
    const summaryMonthlyFixedWidth = 25 + 22 + 22;
    const summaryMonthW =
      (fullTableWidthActivity - summaryMonthlyFixedWidth) / 10;
    doc.autoTable({
      theme: "grid",
      tableWidth: fullTableWidthActivity,
      styles: {
        font: "Amiri",
        halign: "center",
        fontSize: 9,
        valign: "middle",
        fontStyle: "normal",
        lineColor: "#363636",
        lineWidth: 0.1,
        cellPadding: {
          right: 0,
          left: 0,
          top: 1.25,
          bottom: 1.25,
        },
      },
      pageBreak: "avoid",
      body: monthlySummary,
      columnStyles: reverseColumnStyles(
        {
          0: { cellWidth: 25 },
          1: { cellWidth: summaryMonthW },
          2: { cellWidth: summaryMonthW },
          3: { cellWidth: summaryMonthW },
          4: { cellWidth: summaryMonthW },
          5: { cellWidth: summaryMonthW },
          6: { cellWidth: summaryMonthW },
          7: { cellWidth: summaryMonthW },
          8: { cellWidth: summaryMonthW },
          9: { cellWidth: summaryMonthW },
          10: { cellWidth: 22 },
          11: { cellWidth: summaryMonthW },
          12: { cellWidth: 22 },
        },
        13
      ),
      margin: {
        top: 35,
        left: tableMarginHorizontalActivity,
        right: tableMarginHorizontalActivity,
        bottom: LEGEND_FOOTER_HEIGHT_MM,
      },
      didDrawPage: function (data) {
        // Reseting top margin. The change will be reflected only after print the first page.
        data.settings.margin.top = 10;
      },
    });

    // ==============================================
    // SUMMARY: PAIEMENT PAR TRANCHE (ALL LEVELS) – always Tranche 4,3,2,1, full page width
    // ==============================================
    const numTrancheColsSummary = 7; // Total, T4, T3, T2, T1, Levels, Registration (always Tranche 1,2,3,4)

    const trancheSummary = [
      reverseIfNotArabic([
        {
          content: t("encaissement.installmentPayment"),
          colSpan: numTrancheColsSummary,
          styles: { fontStyle: "Bold", fontSize: 10, fillColor: "#e8f4f8" },
        },
      ]),
      // Header row (internal order: ... T1, Registration, Levels → LTR shows Registration before tranches)
      reverseIfNotArabic([
        {
          content: t("scolaritePdf.activities"),
          styles: { fontStyle: "Bold", fontSize: 10 },
        },
        {
          content: t("scolaritePdf.installment", { number: 4 }),
          styles: { fontStyle: "Bold", fontSize: 10 },
        },
        {
          content: t("scolaritePdf.installment", { number: 3 }),
          styles: { fontStyle: "Bold", fontSize: 10 },
        },
        {
          content: t("scolaritePdf.installment", { number: 2 }),
          styles: { fontStyle: "Bold", fontSize: 10 },
        },
        {
          content: t("scolaritePdf.installment", { number: 1 }),
          styles: { fontStyle: "Bold", fontSize: 10 },
        },
        {
          content: t("scolaritePdf.registration"),
          styles: { fontStyle: "Bold", fontSize: 10 },
        },
        {
          content: t("scolaritePdf.levels"),
          styles: { fontStyle: "Bold", fontSize: 10 },
        },
      ]),
    ];

    for (const level of impayeData.levels) {
      const levelTotalPaidTranches = level.tranchesTotalPaid
        ? [4, 3, 2, 1].reduce(
            (acc, n) => acc + (level.tranchesTotalPaid[n] || 0),
            0
          )
        : 0;

      trancheSummary.push(
        reverseIfNotArabic([
          {
            content: this.formatMontant(levelTotalPaidTranches) || "0.000",
            styles: {
              fontSize: 8,
              fillColor: paidColorSummary,
              fontStyle: "Bold",
            },
          },
          {
            content:
              this.formatMontant(level.tranchesTotalPaid?.[4]) || "0.000",
            styles: { fontSize: 8, fillColor: paidColorSummary },
          },
          {
            content:
              this.formatMontant(level.tranchesTotalPaid?.[3]) || "0.000",
            styles: { fontSize: 8, fillColor: paidColorSummary },
          },
          {
            content:
              this.formatMontant(level.tranchesTotalPaid?.[2]) || "0.000",
            styles: { fontSize: 8, fillColor: paidColorSummary },
          },
          {
            content:
              this.formatMontant(level.tranchesTotalPaid?.[1]) || "0.000",
            styles: { fontSize: 8, fillColor: paidColorSummary },
          },
          {
            content:
              this.formatMontant(level.montantTotalInscriptionPaid) || "0.000",
            styles: { fontSize: 8, fillColor: paidColorSummary },
          },
          {
            content: levels[String(level.level)] || level.level,
            styles: { fontSize: 9, fontStyle: "Bold" },
            rowSpan: 2,
          },
        ])
      );

      trancheSummary.push(
        reverseIfNotArabic([
          {
            content: this.formatMontant(level.montantTotalTranches) || "0.000",
            styles: { fontSize: 8, fillColor: colorSummary, fontStyle: "Bold" },
          },
          {
            content: this.formatMontant(level.tranchesTotal?.[4]) || "0.000",
            styles: { fontSize: 8, fillColor: colorSummary },
          },
          {
            content: this.formatMontant(level.tranchesTotal?.[3]) || "0.000",
            styles: { fontSize: 8, fillColor: colorSummary },
          },
          {
            content: this.formatMontant(level.tranchesTotal?.[2]) || "0.000",
            styles: { fontSize: 8, fillColor: colorSummary },
          },
          {
            content: this.formatMontant(level.tranchesTotal?.[1]) || "0.000",
            styles: { fontSize: 8, fillColor: colorSummary },
          },
          {
            content:
              this.formatMontant(level.montantTotalInscription) || "0.000",
            styles: { fontSize: 8, fillColor: colorSummary },
          },
        ])
      );
    }

    const overallTotalPaidTranchesActivity = impayeData.tranchesTotalPaid
      ? [4, 3, 2, 1].reduce(
          (acc, n) => acc + (impayeData.tranchesTotalPaid[n] || 0),
          0
        )
      : 0;

    trancheSummary.push(
      reverseIfNotArabic([
        {
          content:
            this.formatMontant(overallTotalPaidTranchesActivity) || "0.000",
          styles: {
            fontStyle: "Bold",
            fontSize: 10,
            fillColor: paidColorSummary,
          },
        },
        {
          content:
            this.formatMontant(impayeData.tranchesTotalPaid?.[4]) || "0.000",
          styles: {
            fontStyle: "Bold",
            fontSize: 8,
            fillColor: paidColorSummary,
          },
        },
        {
          content:
            this.formatMontant(impayeData.tranchesTotalPaid?.[3]) || "0.000",
          styles: {
            fontStyle: "Bold",
            fontSize: 8,
            fillColor: paidColorSummary,
          },
        },
        {
          content:
            this.formatMontant(impayeData.tranchesTotalPaid?.[2]) || "0.000",
          styles: {
            fontStyle: "Bold",
            fontSize: 8,
            fillColor: paidColorSummary,
          },
        },
        {
          content:
            this.formatMontant(impayeData.tranchesTotalPaid?.[1]) || "0.000",
          styles: {
            fontStyle: "Bold",
            fontSize: 8,
            fillColor: paidColorSummary,
          },
        },
        {
          content:
            this.formatMontant(impayeData.montantTotalInscriptionPaid) ||
            "0.000",
          styles: {
            fontStyle: "Bold",
            fontSize: 10,
            fillColor: paidColorSummary,
          },
        },
        {
          content: t("paymentStatus.totalPaye"),
          styles: { fontSize: 11, lineWidth: 0.1 },
        },
      ]),
      reverseIfNotArabic([
        {
          content:
            this.formatMontant(impayeData.montantTotalTranches) || "0.000",
          styles: { fontStyle: "Bold", fontSize: 10, fillColor: colorSummary },
        },
        {
          content: this.formatMontant(impayeData.tranchesTotal?.[4]) || "0.000",
          styles: { fontStyle: "Bold", fontSize: 8, fillColor: colorSummary },
        },
        {
          content: this.formatMontant(impayeData.tranchesTotal?.[3]) || "0.000",
          styles: { fontStyle: "Bold", fontSize: 8, fillColor: colorSummary },
        },
        {
          content: this.formatMontant(impayeData.tranchesTotal?.[2]) || "0.000",
          styles: { fontStyle: "Bold", fontSize: 8, fillColor: colorSummary },
        },
        {
          content: this.formatMontant(impayeData.tranchesTotal?.[1]) || "0.000",
          styles: { fontStyle: "Bold", fontSize: 8, fillColor: colorSummary },
        },
        {
          content:
            this.formatMontant(impayeData.montantTotalInscription) || "0.000",
          styles: {
            fontStyle: "Bold",
            fontSize: 10,
            fillColor: colorSummary,
          },
        },
        {
          content: t("paymentStatus.totalNonPaye"),
          styles: { fontSize: 11, lineWidth: 0.1 },
        },
      ])
    );
    for (let row = 0; row < trancheSummary.length; row++) {
      trancheSummary[row] = trancheSummary[row].filter(
        (cell) => cell !== cellToDelete
      );
    }

    // Summary tranche: same full width (first 25, Registration 22, Levels 40; 4 tranche cols share the rest)
    const summaryTrancheFixedWidth = 25 + 40 + 22;
    const activitySummaryTrancheW =
      (fullTableWidthActivity - summaryTrancheFixedWidth) / 4;
    const trancheColumnStyles = {
      0: { cellWidth: 25 },
      1: { cellWidth: activitySummaryTrancheW },
      2: { cellWidth: activitySummaryTrancheW },
      3: { cellWidth: activitySummaryTrancheW },
      4: { cellWidth: activitySummaryTrancheW },
      5: { cellWidth: 22 }, // Registration
      6: { cellWidth: 40 }, // Levels
    };

    const trancheStartY =
      doc.lastAutoTable && doc.lastAutoTable.finalY
        ? doc.lastAutoTable.finalY + 8
        : 35;

    doc.autoTable({
      theme: "grid",
      tableWidth: fullTableWidthActivity,
      styles: {
        font: "Amiri",
        halign: "center",
        fontSize: 9,
        valign: "middle",
        fontStyle: "normal",
        lineColor: "#363636",
        lineWidth: 0.1,
        cellPadding: {
          right: 0,
          left: 0,
          top: 1.25,
          bottom: 1.25,
        },
      },
      pageBreak: "avoid",
      startY: trancheStartY,
      body: trancheSummary,
      columnStyles: reverseColumnStyles(
        trancheColumnStyles,
        numTrancheColsSummary
      ),
      margin: {
        left: tableMarginHorizontalActivity,
        right: tableMarginHorizontalActivity,
        bottom: LEGEND_FOOTER_HEIGHT_MM,
      },
    });

    // ==============================================
    // SUMMARY: PAIEMENT UNIQUE (ALL LEVELS) – single-payment activities only
    // ==============================================
    const anyPositive = (obj) =>
      obj && Object.values(obj).some((v) => Number(v) > 0);
    const isSinglePaymentActivitySummary = (activity) => {
      const hasMonthly =
        anyPositive(activity?.monthsTotal) || anyPositive(activity?.monthsTotalPaid);
      const hasTranche =
        anyPositive(activity?.tranchesTotal) ||
        anyPositive(activity?.tranchesTotalPaid);
      return !hasMonthly && !hasTranche;
    };

    const numSingleSummaryCols = 2; // Registration, Levels/Label
    const singlePaymentSummary = [
      reverseIfNotArabic([
        {
          content: t("encaissement.singlePayment"),
          colSpan: numSingleSummaryCols,
          styles: { fontStyle: "Bold", fontSize: 10, fillColor: "#e8f4f8" },
        },
      ]),
      reverseIfNotArabic([
        {
          content: t("scolaritePdf.registration"),
          styles: { fontStyle: "Bold", fontSize: 10 },
        },
        {
          content: t("scolaritePdf.levels"),
          styles: { fontStyle: "Bold", fontSize: 10 },
        },
      ]),
    ];

    let overallSinglePaid = 0;
    let overallSingleUnpaid = 0;
    for (const level of impayeData.levels) {
      const singleActivities = (level.activites || []).filter((a) =>
        isSinglePaymentActivitySummary(a)
      );
      const levelSinglePaid = singleActivities.reduce(
        (acc, a) => acc + (Number(a.montantTotalInscriptionPaid) || 0),
        0
      );
      const levelSingleUnpaid = singleActivities.reduce(
        (acc, a) => acc + (Number(a.montantTotalInscription) || 0),
        0
      );

      overallSinglePaid += levelSinglePaid;
      overallSingleUnpaid += levelSingleUnpaid;

      singlePaymentSummary.push(
        reverseIfNotArabic([
          {
            content: this.formatMontant(levelSinglePaid) || "0.000",
            styles: { fontSize: 8, fillColor: paidColorSummary },
          },
          {
            content: levels[String(level.level)] || level.level,
            styles: { fontSize: 9, fontStyle: "Bold" },
            rowSpan: 2,
          },
        ]),
        reverseIfNotArabic([
          {
            content: this.formatMontant(levelSingleUnpaid) || "0.000",
            styles: { fontSize: 8, fillColor: colorSummary },
          },
        ])
      );
    }

    // Overall totals (paid + unpaid)
    singlePaymentSummary.push(
      reverseIfNotArabic([
        {
          content: this.formatMontant(overallSinglePaid) || "0.000",
          styles: {
            fontStyle: "Bold",
            fontSize: 10,
            fillColor: paidColorSummary,
          },
        },
        {
          content: t("paymentStatus.totalPaye"),
          styles: { fontSize: 11, lineWidth: 0.1 },
        },
      ]),
      reverseIfNotArabic([
        {
          content: this.formatMontant(overallSingleUnpaid) || "0.000",
          styles: { fontStyle: "Bold", fontSize: 10, fillColor: colorSummary },
        },
        {
          content: t("paymentStatus.totalNonPaye"),
          styles: { fontSize: 11, lineWidth: 0.1 },
        },
      ])
    );

    const singlePaymentStartY =
      doc.lastAutoTable && doc.lastAutoTable.finalY
        ? doc.lastAutoTable.finalY + 8
        : 35;
    const singleSummaryRegistrationW = 90;
    const singleSummaryLevelsW =
      fullTableWidthActivity - singleSummaryRegistrationW;

    doc.autoTable({
      theme: "grid",
      tableWidth: fullTableWidthActivity,
      styles: {
        font: "Amiri",
        halign: "center",
        fontSize: 9,
        valign: "middle",
        fontStyle: "normal",
        lineColor: "#363636",
        lineWidth: 0.1,
        cellPadding: {
          right: 0,
          left: 0,
          top: 1.25,
          bottom: 1.25,
        },
      },
      pageBreak: "avoid",
      startY: singlePaymentStartY,
      body: singlePaymentSummary,
      columnStyles: reverseColumnStyles(
        {
          0: { cellWidth: singleSummaryRegistrationW }, // Registration
          1: { cellWidth: singleSummaryLevelsW }, // Levels/Label
        },
        numSingleSummaryCols
      ),
      margin: {
        left: tableMarginHorizontalActivity,
        right: tableMarginHorizontalActivity,
        bottom: LEGEND_FOOTER_HEIGHT_MM,
      },
    });

    //page numbering and legend on each page
    const pages = doc.internal.getNumberOfPages();
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;
    doc.setFontSize(8); //Optional

    for (let j = 1; j < pages + 1; j++) {
      doc.setPage(j);
      this.drawLegendOnPage(doc, pageWidth, pageHeight, t);
      let horizontalPos = pageWidth / 2;
      let verticalPos = pageHeight - 10;
      const pageText = t("pdf.pageNumber", {
        current: j,
        total: pages,
      });
      doc.text(pageText, horizontalPos, verticalPos, {
        align: "center",
      });
    }

    this.context.pdfLoading = false;
    const fileName = t("encaissement.activitiesStateOfUnpaid") + ".pdf";
    doc.save(fileName);
  }
}
